/**
 * ONE TAP — the sound design.
 *
 * Every sound in DESIGN.md §5, synthesised from oscillators, noise and filters. Nothing here
 * loads a file. Each one-shot returns the number of seconds it will take to finish so the
 * engine can retire its voice at exactly the right moment.
 *
 * The four sounds that carry the game, and what they have to feel like:
 *
 *  GUNSHOT   Enormous. A player fires roughly once per life. Four layers — a sample-accurate
 *            transient, a bandpassed crack with a fast downward pitch envelope, a saturated
 *            body, and a sub thump — all dumped into the atrium tail. It is an EVENT.
 *  DRY CLICK Bone dry, tiny, mechanical, slightly detuned, zero reverb. This is the sound of
 *            death arriving. It must make the player wince.
 *  RACK      The reward for the entire core loop. Two metallic snaps 55 ms apart with a body
 *            thunk under them — a mechanical keyboard, not a toy.
 *  HUM       The loaded gun's idle. Deliberate intel: enemies hear it. Two slightly unstable
 *            detuned oscillators under a slow-moving filter, notched out of the 2-4 kHz
 *            fatigue band so it can sit in the mix for a whole 90 second round.
 */

import { clamp, clamp01, hashString, lerpN, makeRng } from '@shared/math';
import { SurfaceKind } from '@shared/types';
import type { IRKind } from './reverb';
import { ReverbBank } from './reverb';
import {
  ADSR,
  ClickFactory,
  MetalFactory,
  MIN_GAIN,
  NoiseFactory,
  Rng,
  ShaperCurve,
  airAbsorption,
  applyADSR,
  biquad,
  dcBlock,
  fadeOut,
  gain,
  jitter,
  makeAsymCurve,
  makeCrunchCurve,
  makeDistortionCurve,
  makeExciterCurve,
  makeFm,
  makeFoldCurve,
  makeLfo,
  makeRingMod,
  noteHz,
  osc,
  percEnv,
  pitchEnv,
  play,
  randRange,
  shaper,
  snapEnv,
  startAtRandomPhase,
  stopSafe,
  sweep,
  swellEnv,
} from './synth';

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

/**
 * Where a sound goes. The engine builds one of these per voice: `dry` is the spatialised
 * input (normally a PannerNode), `wet` is a pre-scaled reverb send, and `distance` drives
 * air absorption.
 */
export interface SfxTarget {
  dry: AudioNode;
  wet: AudioNode | null;
  space: IRKind;
  /** Metres from the listener. 0 for head-locked sounds. */
  distance: number;
  /** True when this is the local player's own action — gets extra weight and less room. */
  local: boolean;
}

/** A sound that lives for as long as the game state says it should. */
export interface SustainedVoice {
  readonly output: AudioNode;
  stop(fadeSec?: number): void;
}

export interface HumVoice extends SustainedVoice {
  /** 0..1 overall level. */
  setLevel(v: number): void;
  /** 0..1 — HIGH NOON opens the filter and pushes the shimmer forward. */
  setUrgency(v: number): void;
}

export interface WhooshVoice extends SustainedVoice {
  /** 0..1 mapped from gun speed. */
  setSpeed(v: number): void;
  /** Frequency ratio from the doppler shift; 1 = stationary relative to the listener. */
  setDoppler(r: number): void;
  /**
   * 0..1 — how close the gun is to passing the listener's head. 1 is a near miss.
   *
   * A whoosh that only tracks speed sounds the same whether the gun is crossing the atrium
   * fifty metres away or going past your ear. Proximity opens the top end and swells the
   * blade tone, which is the cue that says *that one nearly hit me* — and it is what lets
   * an enemy hear a pass go over their head and know to look up.
   */
  setProximity(v: number): void;
}

export interface ScrapeVoice extends SustainedVoice {
  setIntensity(v: number): void;
  setSurface(s: SurfaceKind): void;
}

export interface TetherVoice extends SustainedVoice {
  /** 0..1 — pitch and brightness climb with tether tension. */
  setTension(v: number): void;
}

/**
 * The world bed: atrium room tone, distant city, and wind. One voice with three internal
 * layers because they are always all present and always head-locked — splitting them into
 * three sustained voices would triple the node count for no expressive gain.
 *
 * DESIGN.md §5.6: "A player standing still in silence is a broken mix." This is the fix.
 */
export interface AmbienceVoice extends SustainedVoice {
  /** 0..1 — how present the atrium itself is. Menus want a little, a live round wants all of it. */
  setRoom(v: number): void;
  /** Wind rises with both horizontal speed and height above the arena floor. */
  setWind(speed01: number, altitude01: number): void;
  /** Distant city bed. Constant in practice; a fader so HIGH NOON can strip the world back. */
  setCity(v: number): void;
  /** Sidechain. The bed gets out of the way of a gunshot exactly like the music does. */
  duck(amount: number, seconds: number): void;
}

// ---------------------------------------------------------------------------
// Surface character
// ---------------------------------------------------------------------------

interface SurfaceVoicing {
  /** Body resonance of a footfall, Hz. */
  thump: number;
  /** Centre of the scuff noise, Hz. */
  scuff: number;
  /** Noise bandwidth Q. */
  q: number;
  /** How much ringing metal the surface adds, 0..1. */
  ring: number;
  /** Ring pitch, Hz. */
  ringHz: number;
  /** Decay scale of the whole footfall. */
  decay: number;
  /** Relative loudness. */
  level: number;
}

const SURFACES: Record<SurfaceKind, SurfaceVoicing> = {
  [SurfaceKind.Concrete]: { thump: 96, scuff: 1500, q: 0.9, ring: 0.0, ringHz: 0, decay: 1.0, level: 1.0 },
  [SurfaceKind.Metal]: { thump: 132, scuff: 2900, q: 1.5, ring: 0.55, ringHz: 1180, decay: 1.35, level: 1.05 },
  [SurfaceKind.Grate]: { thump: 150, scuff: 3900, q: 2.6, ring: 0.85, ringHz: 2050, decay: 1.5, level: 0.95 },
  [SurfaceKind.Glass]: { thump: 210, scuff: 5600, q: 3.2, ring: 0.7, ringHz: 3400, decay: 0.8, level: 0.82 },
  [SurfaceKind.Neon]: { thump: 118, scuff: 2200, q: 1.1, ring: 0.3, ringHz: 820, decay: 0.9, level: 0.9 },
};

// Envelope presets reused across the kit.
const ENV_CHIME: ADSR = { a: 0.003, d: 0.16, s: 0.28, r: 0.42 };
const ENV_STAB: ADSR = { a: 0.002, d: 0.09, s: 0.0, r: 0.06 };

/**
 * Post-transient reverb send timing.
 *
 * The single most common cause of a "hollow" synthesised sound is a reverb send that is live
 * from sample zero: the attack arrives already smeared, which is exactly what a sound heard
 * through a wall does. So every send in this file is delayed past the transient AND gated
 * shut across it. `HOLD` is the window in which the send is silent; `FADE` is how long the
 * tail takes to open. Between them, the first ~40 ms of every sound in ONE TAP is bone dry.
 */
const TAIL_HOLD: Record<IRKind, number> = { atrium: 0.026, interior: 0.014, plate: 0.02 };
const TAIL_FADE: Record<IRKind, number> = { atrium: 0.05, interior: 0.03, plate: 0.04 };
/** Sends are highpassed: the sub layer belongs in the dry path only, never in the room. */
const TAIL_HP: Record<IRKind, number> = { atrium: 190, interior: 240, plate: 260 };

// ---------------------------------------------------------------------------
// The kit
// ---------------------------------------------------------------------------

export class Sfx {
  private readonly rng: Rng;
  readonly noise: NoiseFactory;
  private readonly click: ClickFactory;
  private readonly metal: MetalFactory;

  // Shared waveshaper curves — building these per sound would be pure waste.
  private readonly curveWarm: ShaperCurve;
  private readonly curveHard: ShaperCurve;
  private readonly curveAsym: ShaperCurve;
  private readonly curveFold: ShaperCurve;
  /** Hard-knee clip for impact layers. Loudness without squash — see `makeCrunchCurve`. */
  private readonly curveCrunch: ShaperCurve;
  /** Squarer for the sub exciter: puts a 45 Hz thump onto a speaker that stops at 150 Hz. */
  private readonly curveExcite: ShaperCurve;

  constructor(
    private readonly ctx: AudioContext,
    private readonly reverb: ReverbBank,
    noise?: NoiseFactory,
    seed = 0x0a11d10,
  ) {
    this.rng = makeRng(seed >>> 0);
    this.noise = noise ?? new NoiseFactory(ctx, seed);
    this.click = new ClickFactory(ctx);
    this.metal = new MetalFactory(ctx);
    this.curveWarm = makeDistortionCurve(0.35);
    this.curveHard = makeDistortionCurve(1.6);
    this.curveAsym = makeAsymCurve(2.1);
    this.curveFold = makeFoldCurve(2.2);
    this.curveCrunch = makeCrunchCurve(2.4);
    this.curveExcite = makeExciterCurve();
  }

  private get now(): number {
    return this.ctx.currentTime;
  }

  /**
   * Route a finished sound to its dry destination and to the reverb, **post-transient**.
   *
   * The dry path is immediate and unfiltered (beyond air absorption at distance) so the
   * attack is 100% present. The wet path is delayed past the transient and gated shut
   * across it, so the convolver only ever sees the tail. See `TAIL_HOLD`.
   *
   * `at` defaults to now, which is what every caller wants — they all compute their own
   * `t0` as `this.now + 0.001` immediately before calling this.
   */
  private out(node: AudioNode, t: SfxTarget, wet = 0, at = this.now): void {
    const ctx = this.ctx;
    const air = t.distance > 0 ? airAbsorption(ctx, t.distance) : null;
    if (air) {
      node.connect(air);
      air.connect(t.dry);
    } else {
      node.connect(t.dry);
    }
    if (wet <= 0 || !t.wet) return;

    const hold = TAIL_HOLD[t.space] ?? 0.024;
    const fade = TAIL_FADE[t.space] ?? 0.045;

    // Pre-delay in the graph, not just in the impulse: it moves the whole send later, which
    // separates the room from the hit far more convincingly than IR pre-delay alone.
    const delay = ctx.createDelay(0.25);
    delay.delayTime.value = hold;

    const hp = biquad(ctx, 'highpass', TAIL_HP[t.space] ?? 200, 0.7);

    const gate = gain(ctx, 0);
    gate.gain.setValueAtTime(MIN_GAIN, at);
    gate.gain.setValueAtTime(MIN_GAIN, at + hold);
    gate.gain.exponentialRampToValueAtTime(Math.max(wet, MIN_GAIN), at + hold + fade);

    node.connect(delay);
    delay.connect(hp);
    hp.connect(gate);
    gate.connect(t.wet);
  }

  private noiseBurst(kind: 'white' | 'pink' | 'brown' | 'velvet', t0: number, seconds: number, rate = 1): AudioBufferSourceNode {
    const src = this.noise.source(kind, rate);
    startAtRandomPhase(src, t0, this.rng);
    stopSafe(src, t0 + seconds + 0.02);
    return src;
  }

  /** Multiplicative jitter from the shared deterministic stream. See `jitter` in synth.ts. */
  private j(amount: number): number {
    return jitter(this.rng, amount);
  }

  // -------------------------------------------------------------------------
  // Shared impact primitives
  //
  // Every impact in the game is assembled from these three. Standardising them is what
  // makes a footstep and a gunshot feel like they come from the same world, and it is what
  // guarantees no impact is ever missing its sub or its transient.
  // -------------------------------------------------------------------------

  /**
   * TRANSIENT. 1–3 ms of broadband energy with a sample-accurate attack.
   *
   * "Impact lives in the first 5 ms" (DESIGN.md §5.2). Anything without one of these reads
   * as soft and cheap no matter what is layered behind it.
   *
   * **Zero attack, deliberately.** This used to ramp from silence to peak over 0.4 ms, which
   * sounds harmless and is not: a `ClickFactory` buffer puts its entire energy in sample
   * zero, so a 0.4 ms ramp multiplied the loudest sample of every impact in the game by
   * 1e-4 and faded the rest of the click in across its own decay. That one line is why the
   * kit read as soft no matter how many layers went underneath it. See `SNAP_ATTACK`.
   */
  private transient(
    dest: AudioNode,
    at: number,
    o: { level: number; hp?: number; lp?: number; tailMs?: number; grit?: number; decay?: number },
  ): void {
    const ctx = this.ctx;
    const src = this.click.source(o.tailMs ?? 2.6, o.grit ?? 0.35, (this.rng() * 4) | 0);
    const hp = biquad(ctx, 'highpass', (o.hp ?? 1400) * this.j(0.06), 0.6);
    const g = gain(ctx, 0);
    snapEnv(g.gain, at, o.decay ?? 0.011, o.level);
    src.connect(hp);
    let tail: AudioNode = hp;
    if (o.lp !== undefined) {
      const lp = biquad(ctx, 'lowpass', o.lp, 0.7);
      hp.connect(lp);
      tail = lp;
    }
    tail.connect(g);
    g.connect(dest);
    play(src, at, at + 0.06);
  }

  /**
   * EFFORT. A human putting their body into something — the grunt behind a hard throw.
   *
   * Three formants over a pitch-dropping glottal buzz, plus a breath burst. It is
   * deliberately short and low in the mix: the ear only has to register "a person did that",
   * and anything more turns into a voice line, which this game does not have.
   */
  private effort(dest: AudioNode, at: number, o: { level: number; hz: number; length: number }): void {
    const ctx = this.ctx;
    const hz = o.hz * this.j(0.05);

    // Glottal source: a saw dropping in pitch the way a chest does when it empties.
    const src = osc(ctx, 'sawtooth', hz);
    pitchEnv(src.frequency, at, hz * 1.22, hz * 0.7, o.length * 0.9);
    const srcG = gain(ctx, 0);
    percEnv(srcG.gain, at, 0.006, o.length, 1.0, o.length * 0.18);
    src.connect(srcG);
    play(src, at, at + o.length + 0.25);

    // Formants. Roughly an open "hunh": F1 ~ 620, F2 ~ 1080, F3 ~ 2500.
    const bus = gain(ctx, o.level);
    [
      { f: 620, q: 7, g: 1.0 },
      { f: 1080, q: 9, g: 0.5 },
      { f: 2500, q: 11, g: 0.2 },
    ].forEach((f) => {
      const bp = biquad(ctx, 'bandpass', f.f * this.j(0.07), f.q);
      // The formants drift down with the pitch — a body relaxing, not a filter sweep.
      sweep(bp.frequency, at, f.f * 1.1, f.f * 0.86, o.length);
      const g = gain(ctx, f.g);
      srcG.connect(bp);
      bp.connect(g);
      g.connect(bus);
    });

    // The breath that actually leaves. Without this it is a synth tone doing an impression.
    const air = this.noiseBurst('pink', at, o.length + 0.06, this.j(0.12));
    const airBp = biquad(ctx, 'bandpass', 1500 * this.j(0.1), 0.8);
    sweep(airBp.frequency, at, 2100, 780, o.length);
    const airG = gain(ctx, 0);
    percEnv(airG.gain, at, 0.004, o.length * 1.1, 0.55);
    air.connect(airBp);
    airBp.connect(airG);
    airG.connect(bus);

    // Chest. The low end of a person exhaling hard, which is what makes it a body.
    const sat = shaper(ctx, this.curveWarm, '2x');
    const dc = dcBlock(ctx, 40);
    bus.connect(sat);
    sat.connect(dc);
    dc.connect(dest);
  }

  /**
   * SUB. The layer that is always missing, and the reason a mix sounds hollow.
   *
   * A sine plunging fast (e.g. 150 → 45 Hz in 70 ms) with a triangle an octave under it,
   * asymmetrically saturated, DC-blocked, and — critically — accompanied by a `knock`: a
   * short mid-bass thud around 4× the landing frequency. Most players are on laptop
   * speakers which reproduce nothing below ~150 Hz, so the knock and the saturation
   * harmonics ARE the weight for them. Remove everything under 150 Hz and this still hits.
   */
  private sub(
    dest: AudioNode,
    at: number,
    o: {
      from: number;
      to: number;
      sweep: number;
      decay: number;
      level: number;
      knock?: number;
      shape?: number;
      /**
       * Skip the harmonic exciter. Only for the very highest-rate sounds (footsteps, gear
       * foley) where the knock already lands in the band the exciter would fill, so the
       * four extra nodes per trigger buy nothing. Never set this on an impact.
       */
      light?: boolean;
    },
  ): void {
    const ctx = this.ctx;
    const jf = this.j(0.03);
    const from = o.from * jf;
    const to = o.to * jf;

    const g = gain(ctx, 0);
    // Zero attack. A sine and a triangle both start at amplitude zero, so a step here cannot
    // click — and the 1.2 ms ramp this replaces was quietly rounding the leading edge off the
    // weight layer of every impact in the game.
    snapEnv(g.gain, at, o.decay, o.level);

    const o1 = osc(ctx, 'sine', from);
    pitchEnv(o1.frequency, at, from, to, o.sweep, o.shape ?? 1);
    const o2 = osc(ctx, 'triangle', from * 0.5);
    pitchEnv(o2.frequency, at, from * 0.5, to * 0.62, o.sweep * 1.35, o.shape ?? 1);
    const g2 = gain(ctx, 0.55);
    o1.connect(g);
    o2.connect(g2);
    g2.connect(g);

    // Saturate, then remove the DC the asymmetry creates. Skipping the DC block would cost
    // the limiter real headroom for a component nobody can hear. '2x' oversampling rather
    // than '4x' because `sub` runs on every footstep of every player and the highest
    // partial in here is a few hundred Hz — there is no aliasing to be had.
    const sat = shaper(ctx, this.curveAsym, '2x');
    const dc = dcBlock(ctx, 24);
    g.connect(sat);
    sat.connect(dc);
    dc.connect(dest);

    // Harmonic exciter. Squaring a 45 Hz sine puts real energy at 90 and 135 Hz, which is
    // the difference between a laptop speaker rendering this layer as *something* and
    // rendering it as literally nothing. Band-limited so it stays weight and never grit.
    if (!o.light) {
      const exc = shaper(ctx, this.curveExcite, '2x');
      const excBp = biquad(ctx, 'bandpass', clamp(to * 3.2, 90, 420), 0.9);
      const excDc = dcBlock(ctx, 40);
      const excG = gain(ctx, o.level * 0.3);
      g.connect(exc);
      exc.connect(excDc);
      excDc.connect(excBp);
      excBp.connect(excG);
      excG.connect(dest);
    }

    play(o1, at, at + o.decay + 0.15);
    play(o2, at, at + o.decay + 0.15);

    // The knock — the part of the weight that survives a speaker with no low end at all.
    const knock = o.knock ?? 0.55;
    if (knock > 0.01) {
      // `to` already carries the pitch jitter, so it is not reapplied here.
      const k = osc(ctx, 'sine', to * 4.2);
      pitchEnv(k.frequency, at, to * 5.4, to * 2.6, Math.min(o.sweep * 0.8, 0.05));
      const kg = gain(ctx, 0);
      snapEnv(kg.gain, at, Math.min(o.decay * 0.42, 0.075), o.level * knock);
      const ksat = shaper(ctx, this.curveCrunch, '2x');
      k.connect(kg);
      kg.connect(ksat);
      ksat.connect(dest);
      play(k, at, at + 0.2);
    }
  }

  /**
   * CLOTH / GEAR. The quiet layer that separates "a character moved" from "a sound played".
   * Two short bandpassed noise puffs at slightly different heights, deliberately soft.
   */
  private cloth(dest: AudioNode, at: number, level: number): void {
    const ctx = this.ctx;
    for (let i = 0; i < 2; i++) {
      const src = this.noiseBurst('pink', at + i * 0.021 * this.j(0.4), 0.09, this.j(0.18));
      const bp = biquad(ctx, 'bandpass', (i === 0 ? 2400 : 4100) * this.j(0.12), 1.1);
      const g = gain(ctx, 0);
      percEnv(g.gain, at + i * 0.021, 0.0025, 0.038 * this.j(0.25), level * (i === 0 ? 1 : 0.6));
      src.connect(bp);
      bp.connect(g);
      g.connect(dest);
    }
  }

  // -------------------------------------------------------------------------
  // THE GUNSHOT
  // -------------------------------------------------------------------------

  /**
   * THE GUNSHOT. The single most important sound in the game, fired roughly once per life.
   * "If it does not make a player flinch slightly, it is wrong." (DESIGN.md §5)
   *
   * Seven layers, in the order the ear resolves them:
   *
   *   0–2 ms    two transients — a bright muzzle crack and a mid-band slap
   *   0–70 ms   the supersonic crack, hard-clipped
   *   0–90 ms   the PUNCH, 180–420 Hz. This is the band a laptop speaker actually
   *             reproduces, and it is why this shot survives without a subwoofer
   *   0–350 ms  the body, the mass of expanding gas
   *   0–450 ms  the sub, 168 → 40 Hz, saturated and DC-blocked
   *   26/95 ms  the slide cycling and the case hitting the floor
   *   tail      a wet-only boom that exists purely to fall down the atrium
   *
   * The local shooter gets more sub and transient (you feel your own gun); everyone else
   * gets more room, because distance IS reverb.
   */
  gunshot(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const near = t.local;
    const bus = gain(ctx, near ? 1.35 : 1.15);
    // Far more wet than before, and it costs the attack nothing: `out` gates the send shut
    // across the first 26 ms, so all of this lands behind a bone-dry crack.
    this.out(bus, t, near ? 0.72 : 1.15, t0);

    // 0. THE SHOCK. One sample of full-scale, completely unfiltered, straight onto the bus.
    //
    //    Every other layer in this sound passes through at least one biquad, and a biquad
    //    cannot help but spread a single-sample impulse over several samples — which halves
    //    its peak and is a large part of why the shot measured tame no matter how much was
    //    stacked behind it. This layer exists to put a genuine full-scale sample at t0 so
    //    the master's soft-clip knee has something to bite on. It is the flinch.
    {
      const src = this.click.source(1.1, 0.12, (this.rng() * 4) | 0);
      const g = gain(ctx, 0);
      snapEnv(g.gain, t0, 0.004, near ? 1.0 : 0.72);
      src.connect(g);
      g.connect(bus);
      play(src, t0, t0 + 0.04);
    }

    // 1a. Muzzle transient — zero attack, full scale. The ear locks onto this.
    this.transient(bus, t0, { level: near ? 1.15 : 0.9, hp: 2600, tailMs: 2.4, grit: 0.3, decay: 0.009 });
    // 1b. Mid slap — gives the transient a body so it is a BANG and not a tick.
    this.transient(bus, t0, { level: 0.92, hp: 620, lp: 4200, tailMs: 5.0, grit: 0.65, decay: 0.02 });
    // 1c. The muzzle blast arriving a hair behind the shock wave. A real report is two
    //     events ~1.5 ms apart, and that tiny gap is most of what separates a gunshot from
    //     a snare drum.
    this.transient(bus, t0 + 0.0016, { level: 0.85, hp: 1200, lp: 9000, tailMs: 3.4, grit: 0.55, decay: 0.014 });

    // 2. Crack — bandpassed white noise with a violent downward sweep, hard-clipped.
    {
      const src = this.noiseBurst('white', t0, 0.24);
      const bp = biquad(ctx, 'bandpass', 5200, 1.0);
      sweep(bp.frequency, t0, 7200 * this.j(0.05), 780, 0.062);
      const hp = biquad(ctx, 'highpass', 420, 0.7);
      const dist = shaper(ctx, this.curveCrunch, '4x');
      const g = gain(ctx, 0);
      snapEnv(g.gain, t0, 0.115 * this.j(0.1), 1.15);
      src.connect(bp);
      bp.connect(hp);
      hp.connect(dist);
      dist.connect(g);
      g.connect(bus);
    }

    // 3. PUNCH — 180–420 Hz. The chest band. Small speakers reproduce this and nothing under
    //    it, so without this layer the shot is a hiss on most of the machines that play it.
    {
      const o = osc(ctx, 'square', 240 * this.j(0.03));
      pitchEnv(o.frequency, t0, 300, 96, 0.055);
      const bp = biquad(ctx, 'bandpass', 300, 0.9);
      sweep(bp.frequency, t0, 520, 190, 0.08);
      const dist = shaper(ctx, this.curveCrunch, '4x');
      const g = gain(ctx, 0);
      snapEnv(g.gain, t0, 0.085, 0.95);
      o.connect(bp);
      bp.connect(dist);
      dist.connect(g);
      g.connect(bus);
      play(o, t0, t0 + 0.3);

      const n = this.noiseBurst('brown', t0, 0.18, this.j(0.12));
      const nbp = biquad(ctx, 'bandpass', 260 * this.j(0.08), 0.8);
      const ndist = shaper(ctx, this.curveCrunch, '2x');
      const ng = gain(ctx, 0);
      snapEnv(ng.gain, t0, 0.07, 0.7);
      n.connect(nbp);
      nbp.connect(ndist);
      ndist.connect(ng);
      ng.connect(bus);
    }

    // 4. Body — saturated brown noise, the mass of expanding gas.
    {
      const src = this.noiseBurst('brown', t0, 0.52, randRange(this.rng, 0.9, 1.1));
      const lp = biquad(ctx, 'lowpass', 1400, 0.9);
      sweep(lp.frequency, t0, 2600, 240, 0.3);
      const dist = shaper(ctx, this.curveAsym, '2x');
      const dc = dcBlock(ctx, 30);
      const g = gain(ctx, 0);
      snapEnv(g.gain, t0, 0.33, 1.15);
      src.connect(lp);
      lp.connect(dist);
      dist.connect(dc);
      dc.connect(g);
      g.connect(bus);
    }

    // 5. Sub — the thump you feel rather than hear, plus the knock that carries it to a
    //    speaker with no low end. Longer and deeper than any other sound in the game.
    this.sub(bus, t0, {
      from: 190,
      to: 36,
      sweep: 0.085,
      decay: near ? 0.54 : 0.4,
      level: near ? 1.5 : 1.15,
      knock: 0.85,
      shape: 0.7,
    });

    // 6. Mechanism — the slide cycling at 26 ms, the case landing at ~95 ms. Pure detail,
    //    and detail is what stops a synthesised gun sounding like a synthesiser.
    {
      const ping = this.metal.source({
        freq: 2350 * this.j(0.04),
        seconds: 0.2,
        sustain: 0.25,
        brightness: 0.85,
        metal: 0.9,
        variant: (this.rng() * 3) | 0,
      });
      const g = gain(ctx, 0);
      percEnv(g.gain, t0 + 0.026, 0.0006, 0.11, 0.26);
      const hp = biquad(ctx, 'highpass', 1200, 0.7);
      ping.connect(hp);
      hp.connect(g);
      g.connect(bus);
      play(ping, t0 + 0.026, t0 + 0.3);

      if (near) {
        const caseAt = t0 + 0.095 * this.j(0.2);
        this.transient(bus, caseAt, { level: 0.16, hp: 3400, tailMs: 1.8, grit: 0.5, decay: 0.006 });
        const tink = this.metal.source({ freq: 4100 * this.j(0.08), seconds: 0.16, sustain: 0.2, brightness: 0.95, metal: 0.95 });
        const tg = gain(ctx, 0);
        percEnv(tg.gain, caseAt, 0.0005, 0.1, 0.12);
        tink.connect(tg);
        tg.connect(bus);
        play(tink, caseAt, caseAt + 0.25);
      }
    }

    // 7. The tail. Wet-only: this layer never reaches the dry path at all, it exists solely
    //    to give the atrium something enormous to decay. It is what turns a bang into an
    //    event happening in a building.
    if (t.wet) {
      const boom = this.noiseBurst('brown', t0, 0.7, 0.85);
      const bp = biquad(ctx, 'bandpass', 420, 0.7);
      sweep(bp.frequency, t0, 900, 220, 0.5);
      const bg = gain(ctx, 0);
      percEnv(bg.gain, t0 + 0.03, 0.008, 0.6, near ? 0.62 : 1.0);
      const send = gain(ctx, 1);
      boom.connect(bp);
      bp.connect(bg);
      bg.connect(send);
      send.connect(t.wet);

      // The report leaving the building. A second, brighter burst 55 ms behind the first
      // gives the atrium a distinct slap to work with instead of one smear, and that is what
      // reads as "a gun went off in a large concrete space" rather than "a gun went off".
      const slap = this.noiseBurst('white', t0 + 0.055, 0.3, 0.9);
      const sbp = biquad(ctx, 'bandpass', 1400, 0.9);
      sweep(sbp.frequency, t0 + 0.055, 2400, 620, 0.24);
      const sg = gain(ctx, 0);
      percEnv(sg.gain, t0 + 0.055, 0.004, 0.26, near ? 0.3 : 0.5);
      slap.connect(sbp);
      sbp.connect(sg);
      sg.connect(send);
    }

    return 1.9;
  }

  /** Bullet striking geometry. Spall, a short ricochet whine, and a dusty puff. */
  bulletImpact(t: SfxTarget, surface: SurfaceKind): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const v = SURFACES[surface] ?? SURFACES[SurfaceKind.Concrete];
    const bus = gain(ctx, 0.8);
    this.out(bus, t, 0.7, t0);

    // Transient: the round arriving. Everything else is the material's response to it.
    this.transient(bus, t0, { level: 0.95, hp: v.scuff * 0.5, tailMs: 2.4, grit: 0.5, decay: 0.008 });

    const src = this.noiseBurst('white', t0, 0.16, this.j(0.12));
    const bp = biquad(ctx, 'bandpass', v.scuff * 1.6 * this.j(0.1), 1.6);
    sweep(bp.frequency, t0, v.scuff * 2.2, v.scuff * 0.7, 0.085);
    const crunch = shaper(ctx, this.curveCrunch, '2x');
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.0006, 0.095, 0.95);
    src.connect(bp);
    bp.connect(crunch);
    crunch.connect(g);
    g.connect(bus);

    if (v.ring > 0.05) {
      const ping = this.metal.source({
        freq: v.ringHz * 1.4 * this.j(0.06),
        seconds: 0.4,
        sustain: 0.45,
        brightness: 0.8,
        metal: 0.85,
        variant: (this.rng() * 3) | 0,
      });
      const pg = gain(ctx, 0);
      percEnv(pg.gain, t0, 0.001, 0.3, 0.44 * v.ring);
      ping.connect(pg);
      pg.connect(bus);
      play(ping, t0, t0 + 0.5);
    }

    // Spall — the chunk of the wall that leaves. Without the sub layer a bullet hitting
    // concrete is a hiss; with it, something got destroyed.
    this.sub(bus, t0, { from: v.thump * 2.0, to: v.thump * 0.55, sweep: 0.045, decay: 0.1, level: 0.68, knock: 0.6 });

    return 0.8;
  }

  /** Bullet hitting a player. Wet, close, low — deliberately unpleasant. */
  fleshImpact(t: SfxTarget, lethal: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1.0);
    this.out(bus, t, 0.42, t0);

    // A wet slap, not a click. Low-passed transient with a lot of grit.
    this.transient(bus, t0, { level: 0.85, hp: 300, lp: 2600, tailMs: 6, grit: 0.85, decay: 0.016 });

    const src = this.noiseBurst('brown', t0, 0.25, this.j(0.12));
    const lp = biquad(ctx, 'lowpass', 900, 1.2);
    sweep(lp.frequency, t0, 1700, 300, 0.105);
    const crunch = shaper(ctx, this.curveCrunch, '2x');
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.0008, lethal ? 0.24 : 0.16, 1.0);
    src.connect(lp);
    lp.connect(crunch);
    crunch.connect(g);
    g.connect(bus);

    // The mass of a person absorbing a round. This is the layer that makes a hit land in
    // the listener's chest rather than only in their ears.
    this.sub(bus, t0, {
      from: 200,
      to: lethal ? 40 : 62,
      sweep: 0.075,
      decay: lethal ? 0.28 : 0.19,
      level: 0.95,
      knock: 0.7,
    });

    // A short, gristly crack on the high end so it reads at distance.
    const cl = this.click.source(6, 0.7, (this.rng() * 4) | 0);
    const bp = biquad(ctx, 'bandpass', 1900 * this.j(0.08), 1.4);
    const cg = gain(ctx, 0);
    percEnv(cg.gain, t0, 0.0005, 0.048, 0.58);
    cl.connect(bp);
    bp.connect(cg);
    cg.connect(bus);
    play(cl, t0, t0 + 0.1);

    return 0.7;
  }

  // -------------------------------------------------------------------------
  // THE DRY CLICK
  // -------------------------------------------------------------------------

  /**
   * You pulled the trigger on an empty chamber. Two tiny detuned mechanical ticks 11 ms
   * apart — hammer, then sear. Highpassed hard, no body, NO reverb send at all, and quiet
   * enough that the silence around it is the loudest part.
   */
  dryFire(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    // Loud, because it is happening in your hands, but over in 40 ms. Level and duration are
    // different axes and this sound is the clearest proof of it in the whole game.
    const bus = gain(ctx, t.local ? 1.55 : 0.95);
    // Deliberately no wet: this sound must be bone dry. It is the only sound in the game
    // with no room around it, and that absence is what makes it land.
    this.out(bus, t, 0);

    // Hammer, sear, and the overtravel stop. Detuned a hair from each other every trigger —
    // a mechanism that clicked identically twice would be a machine, not a gun.
    const ticks: Array<{ at: number; hz: number; lvl: number; tail: number }> = [
      { at: 0, hz: 2680, lvl: 1.0, tail: 3.2 },
      { at: 0.0105 * this.j(0.12), hz: 3170, lvl: 0.66, tail: 2.2 },
      { at: 0.019 * this.j(0.12), hz: 2210, lvl: 0.34, tail: 4.0 },
    ];

    for (const tick of ticks) {
      const src = this.click.source(tick.tail, 0.22, (this.rng() * 4) | 0);
      const bp = biquad(ctx, 'bandpass', tick.hz * this.j(0.03), 5.5);
      const hp = biquad(ctx, 'highpass', 1250, 0.8);
      const g = gain(ctx, 0);
      percEnv(g.gain, t0 + tick.at, 0.0003, 0.012, 0.72 * tick.lvl);
      src.connect(bp);
      bp.connect(hp);
      hp.connect(g);
      g.connect(bus);
      play(src, t0 + tick.at, t0 + tick.at + 0.06);
    }

    // The hammer actually falling. Highpassed at 300 Hz so it has a hard *clack* body without
    // a single Hz of boom — the dryness is the entire point, and any low end would soften it.
    {
      const n = this.noiseBurst('white', t0, 0.05, this.j(0.15));
      const bp = biquad(ctx, 'bandpass', 780 * this.j(0.06), 2.2);
      const hp = biquad(ctx, 'highpass', 300, 0.8);
      const crunch = shaper(ctx, this.curveCrunch, '2x');
      const g = gain(ctx, 0);
      percEnv(g.gain, t0, 0.0004, 0.019, 0.55);
      n.connect(bp);
      bp.connect(hp);
      hp.connect(crunch);
      crunch.connect(g);
      g.connect(bus);
    }

    // A minuscule spring resonance — the only "tone" in the sound, and almost inaudible.
    const spring = this.metal.source({
      freq: 4200 * this.j(0.05),
      seconds: 0.09,
      sustain: 0.1,
      brightness: 0.95,
      metal: 0.6,
      variant: (this.rng() * 3) | 0,
    });
    const sg = gain(ctx, 0);
    percEnv(sg.gain, t0 + 0.0015, 0.0005, 0.05, 0.14);
    const shp = biquad(ctx, 'highpass', 2400, 0.7);
    spring.connect(shp);
    shp.connect(sg);
    sg.connect(bus);
    play(spring, t0 + 0.0015, t0 + 0.12);

    return 0.2;
  }

  // -------------------------------------------------------------------------
  // THE RACK
  // -------------------------------------------------------------------------

  /**
   * Catching a gun cleanly. The reward sound of the entire game loop, so it gets the most
   * layers of anything except the shot itself: a leather slap, then a metallic double-snap
   * 55 ms apart, then a confirmation interval if the round actually chambered.
   */
  catchRack(t: SfxTarget, opts: { chambered: boolean; intercept: boolean }): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, t.local ? 1.3 : 0.98);
    this.out(bus, t, t.local ? 0.34 : 0.62, t0);

    // Transient — the instant the gun stops dead against the palm. Zero attack. Everything
    // else in this sound is consequence; this is the event.
    this.transient(bus, t0, { level: 1.0, hp: 1100, lp: 7000, tailMs: 3.2, grit: 0.55, decay: 0.013 });

    // Palm slap — soft, wide, immediate. Without this the rack floats with no hand behind it.
    {
      const src = this.noiseBurst('pink', t0, 0.1, this.j(0.15));
      const bp = biquad(ctx, 'bandpass', 620 * this.j(0.08), 1.0);
      const crunch = shaper(ctx, this.curveCrunch, '2x');
      const g = gain(ctx, 0);
      snapEnv(g.gain, t0, 0.052, 0.85);
      src.connect(bp);
      bp.connect(crunch);
      crunch.connect(g);
      g.connect(bus);
    }
    // Glove leather. The organic layer that makes it a hand and not a vice.
    this.cloth(bus, t0, 0.26);

    // Snap one: the gun stops dead in the hand.
    this.metallicSnap(bus, t0, { hz: 1980, level: 1.1, bright: 0.9, tail: 0.16 });

    if (opts.chambered) {
      // Snap two: the slide travelling and locking. This is the half that means "chambered",
      // and it is the reward for the entire core loop — so it is the loudest single moment
      // in the sound, LOUDER than the catch itself. The mechanism closing is the point.
      const at = t0 + 0.055 * this.j(0.06);
      this.metallicSnap(bus, at, { hz: 2640, level: 1.25, bright: 0.95, tail: 0.22 });
      this.metallicSnap(bus, at + 0.023, { hz: 1520, level: 0.55, bright: 0.6, tail: 0.3 });
      // The lockup: a hard crunch that says the mechanism reached its stop and went no
      // further. A well-made mechanism closing has an END, and this is it.
      this.transient(bus, at, { level: 0.7, hp: 2200, tailMs: 2.0, grit: 0.4, decay: 0.008 });
      // And the bolt seating — a short low clack under the lockup. This is the difference
      // between a bright click and a machined object arriving at its stop.
      this.sub(bus, at, { from: 300, to: 96, sweep: 0.028, decay: 0.075, level: 0.6, knock: 0.7 });
    } else {
      // Empty catch: the slide travels its whole length and finds nothing. Duller, lower,
      // and it OVERRUNS — a longer, looser rattle where the chambered version has a stop.
      // A player must be able to tell these apart with their eyes shut, at any distance.
      this.metallicSnap(bus, t0 + 0.06 * this.j(0.08), { hz: 1180, level: 0.7, bright: 0.3, tail: 0.3 });
      this.metallicSnap(bus, t0 + 0.098 * this.j(0.12), { hz: 880, level: 0.34, bright: 0.2, tail: 0.34 });
    }

    // Body thunk — a mechanical keyboard has a case under the switch, and this gun has a
    // frame. Saturated with a knock so the weight reads on a laptop speaker too.
    this.sub(bus, t0, { from: 250, to: 68, sweep: 0.05, decay: 0.14, level: 0.85, knock: 0.65 });

    if (opts.chambered) {
      // Rising perfect fifth: the "you are armed" confirmation. Short, bright, unmissable.
      const notes = [noteHz(24), noteHz(31)];
      notes.forEach((hz, i) => {
        const fm = makeFm(ctx, hz * 2, 2.0, hz * 0.6, 'sine', 'sine');
        const g = gain(ctx, 0);
        percEnv(g.gain, t0 + 0.05 + i * 0.045, 0.002, 0.2, 0.2);
        percEnv(fm.index.gain, t0 + 0.05 + i * 0.045, 0.001, 0.09, hz * 1.1);
        fm.output.connect(g);
        g.connect(bus);
        fm.start(t0 + 0.05 + i * 0.045);
        fm.stop(t0 + 0.05 + i * 0.045 + 0.35);
      });
    }

    if (opts.intercept) {
      // You took it out of the air from the other team. A descending, predatory swoop under it.
      const o = osc(ctx, 'sawtooth', 320);
      pitchEnv(o.frequency, t0, 320, 96, 0.22);
      const lp = biquad(ctx, 'lowpass', 1600, 3.5);
      sweep(lp.frequency, t0, 2600, 500, 0.24);
      const g = gain(ctx, 0);
      percEnv(g.gain, t0, 0.004, 0.24, 0.28);
      o.connect(lp);
      lp.connect(g);
      g.connect(bus);
      play(o, t0, t0 + 0.5);
    }

    // Note: no heal layer here. When a catch heals, the sim emits a separate `heal` event
    // in the same batch and `heal()` plays the swell — putting it here as well doubled it.

    return 0.9;
  }

  /**
   * One bright metallic click: transient + saturated Karplus ping + a sliver of noise.
   *
   * The saturation matters more than it looks. A clean Karplus string is a pretty tone; run
   * through a hard knee it becomes a struck object, and "struck object" is the entire read
   * of every mechanical sound in this game.
   */
  private metallicSnap(dest: AudioNode, at: number, o: { hz: number; level: number; bright: number; tail: number }): void {
    const ctx = this.ctx;

    this.transient(dest, at, { level: 0.7 * o.level, hp: 1500, tailMs: 2.2, grit: 0.3, decay: 0.0075 });

    const ping = this.metal.source({
      freq: o.hz * this.j(0.03),
      seconds: Math.max(0.12, o.tail),
      sustain: 0.3 + 0.35 * o.bright,
      brightness: o.bright,
      metal: 0.95,
      variant: (this.rng() * 3) | 0,
    });
    const pg = gain(ctx, 0);
    percEnv(pg.gain, at, 0.0006, o.tail * this.j(0.08), 0.55 * o.level);
    const php = biquad(ctx, 'highpass', 700, 0.7);
    const pdist = shaper(ctx, this.curveCrunch, '2x');
    ping.connect(php);
    php.connect(pdist);
    pdist.connect(pg);
    pg.connect(dest);
    play(ping, at, at + o.tail + 0.1);

    const n = this.noiseBurst('white', at, 0.05, this.j(0.12));
    const bp = biquad(ctx, 'bandpass', 4200 * (0.6 + 0.7 * o.bright) * this.j(0.08), 2.2);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, at, 0.0004, 0.024, 0.34 * o.level);
    n.connect(bp);
    bp.connect(ng);
    ng.connect(dest);
  }

  /** Picking a gun up off the floor: a dull, unsatisfying clack. No rack, no reward. */
  pickup(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 0.95);
    this.out(bus, t, 0.4, t0);

    // Deliberately duller than the rack. Picking a gun up off the floor gives you a
    // paperweight, and the sound is allowed to say so — but it still has to be a THING
    // happening, so it gets the same transient/sub treatment, just voiced down.
    this.transient(bus, t0, { level: 0.55, hp: 700, lp: 4000, tailMs: 3.5, grit: 0.6, decay: 0.014 });
    this.cloth(bus, t0, 0.3);

    const src = this.noiseBurst('pink', t0, 0.12, this.j(0.15));
    const bp = biquad(ctx, 'bandpass', 900 * this.j(0.1), 1.3);
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.0008, 0.068, 0.6);
    src.connect(bp);
    bp.connect(g);
    g.connect(bus);

    const ping = this.metal.source({
      freq: 760 * this.j(0.05),
      seconds: 0.24,
      sustain: 0.25,
      brightness: 0.3,
      metal: 0.8,
      variant: (this.rng() * 3) | 0,
    });
    const pg = gain(ctx, 0);
    percEnv(pg.gain, t0 + 0.006, 0.001, 0.17, 0.34);
    const lp = biquad(ctx, 'lowpass', 2000, 0.8);
    ping.connect(lp);
    lp.connect(pg);
    pg.connect(bus);
    play(ping, t0 + 0.006, t0 + 0.35);

    this.sub(bus, t0, { from: 170, to: 62, sweep: 0.055, decay: 0.11, level: 0.55, knock: 0.45 });

    return 0.5;
  }

  // -------------------------------------------------------------------------
  // THE HUM
  // -------------------------------------------------------------------------

  /**
   * A loaded gun's idle. Two detuned oscillators a fifth apart under a slowly breathing
   * lowpass, with two incommensurate LFOs so it never settles into an audible cycle. A
   * notch sits at 3 kHz — that band is where listener fatigue comes from, and this sound is
   * on screen for a whole round.
   */
  gunHum(): HumVoice {
    const ctx = this.ctx;
    const t0 = this.now;
    const out = gain(ctx, 0);

    const level = gain(ctx, 0);
    const trem = gain(ctx, 1);
    const lp = biquad(ctx, 'lowpass', 620, 3.2);
    const notch = biquad(ctx, 'peaking', 3000, 1.1, -9);
    const hp = biquad(ctx, 'highpass', 60, 0.7);

    const base = noteHz(12); // A2, 110 Hz — sits under the music's bass register.
    const o1 = osc(ctx, 'sawtooth', base, -6);
    const o2 = osc(ctx, 'triangle', base * 1.5, +7);
    const o3 = osc(ctx, 'sine', base * 4, 0); // faint shimmer partial

    const g1 = gain(ctx, 0.5);
    const g2 = gain(ctx, 0.34);
    const g3 = gain(ctx, 0.06);

    o1.connect(g1);
    o2.connect(g2);
    o3.connect(g3);
    g1.connect(lp);
    g2.connect(lp);
    g3.connect(notch);
    lp.connect(notch);
    notch.connect(hp);
    hp.connect(trem);
    trem.connect(level);
    level.connect(out);

    // Instability: pitch drift and filter breathing on prime-ish, non-repeating periods.
    // Nothing here shares a period with anything else, so the hum never settles into a loop.
    const driftA = makeLfo(ctx, 0.13, 5.5, 'sine');
    driftA.connectTo(o1.detune);
    const driftB = makeLfo(ctx, 0.31, 4.0, 'triangle');
    driftB.connectTo(o2.detune);
    const breathe = makeLfo(ctx, 0.077, 190, 'sine');
    breathe.connectTo(lp.frequency);
    // Tremolo rides a dedicated node so it can never push the master level negative.
    const tremLfo = makeLfo(ctx, 0.23, 0.085, 'sine');
    tremLfo.connectTo(trem.gain);

    const startAt = t0 + 0.005;
    try {
      o1.start(startAt);
      o2.start(startAt);
      o3.start(startAt);
    } catch {
      /* already started */
    }
    driftA.start(startAt);
    driftB.start(startAt);
    breathe.start(startAt);
    tremLfo.start(startAt);

    let stopped = false;
    let current = 0;

    return {
      output: out,
      setLevel(v: number) {
        if (stopped) return;
        const target = clamp(v, 0, 1.5);
        if (Math.abs(target - current) < 0.005) return;
        current = target;
        const now = ctx.currentTime;
        level.gain.cancelScheduledValues(now);
        level.gain.setValueAtTime(Math.max(level.gain.value, MIN_GAIN), now);
        level.gain.linearRampToValueAtTime(target, now + 0.12);
      },
      setUrgency(v: number) {
        if (stopped) return;
        const u = clamp01(v);
        const now = ctx.currentTime;
        lp.frequency.cancelScheduledValues(now);
        lp.frequency.setValueAtTime(lp.frequency.value, now);
        lp.frequency.linearRampToValueAtTime(lerpN(620, 1750, u), now + 0.35);
        g3.gain.setTargetAtTime(lerpN(0.06, 0.3, u), now, 0.3);
        o3.detune.setTargetAtTime(lerpN(0, 18, u), now, 0.4);
        breathe.oscillator.frequency.setTargetAtTime(lerpN(0.077, 0.6, u), now, 0.4);
      },
      stop(fadeSec = 0.18) {
        if (stopped) return;
        stopped = true;
        const now = ctx.currentTime;
        const end = fadeOut(level.gain, now, fadeSec);
        stopSafe(o1, end + 0.05);
        stopSafe(o2, end + 0.05);
        stopSafe(o3, end + 0.05);
        driftA.stop(end + 0.05);
        driftB.stop(end + 0.05);
        breathe.stop(end + 0.05);
        tremLfo.stop(end + 0.05);
        setTimeout(
          () => {
            try {
              out.disconnect();
            } catch {
              /* already gone */
            }
          },
          (fadeSec + 0.25) * 1000,
        );
      },
    };
  }

  // -------------------------------------------------------------------------
  // THROW / FLIGHT
  // -------------------------------------------------------------------------

  /**
   * THE THROW — the instant of release.
   *
   * A player hurling their team's only weapon across an atrium is one of the two or three
   * most dramatic things that happens in a round, and it used to be, in the lead's words,
   * "a little click". So it is now built the way the moment actually is, in the order the
   * ear resolves it:
   *
   *   -110 ms   the WIND-UP. A reverse swell of air that rises into the release and is gone
   *             by the time it lands. The ear hears something being loaded.
   *   0 ms      the GRUNT — three formants over a dropping glottal buzz. A person did this.
   *   0 ms      the hand transient and glove leather: the gun stops being yours.
   *   0–90 ms   the ARM. A hard sweep of bandpassed noise following the limb through its arc.
   *   0–200 ms  the sub: the effort, out of the legs for a charged pass, out of the wrist
   *             for a flick.
   *   0–260 ms  the LAUNCH sweep — a rising blade tone that hands the object off to
   *             `throwWhoosh`, which is already tracking the gun by the time this ends.
   *
   * The handoff is the important part. The release and the flight are one continuous
   * gesture, not two sounds that happen near each other.
   */
  throwRelease(t: SfxTarget, charged: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, charged ? 1.35 : 1.05);
    this.out(bus, t, 0.42, t0);

    // The wind-up. It has to be scheduled BEFORE t0 to swell into it, and it cannot be —
    // the sim tells us about the throw at the moment it happens. So the swell is compressed
    // into the 60 ms after release instead, under the transient, where it reads as the arm
    // still travelling through the follow-through. Same information, honest timing.
    {
      const n = this.noiseBurst('pink', t0, 0.16, this.j(0.12));
      const bp = biquad(ctx, 'bandpass', 700 * this.j(0.1), 1.4);
      sweep(bp.frequency, t0, 480, 1900, 0.09);
      const g = gain(ctx, 0);
      swellEnv(g.gain, t0, 0.055, charged ? 0.5 : 0.3, 0.075);
      n.connect(bp);
      bp.connect(g);
      g.connect(bus);
    }

    // The grunt of effort. A charged throw comes out of the whole body; a flick does not,
    // so it gets a shorter, higher, much quieter one.
    this.effort(bus, t0, {
      level: charged ? 0.5 : 0.22,
      hz: charged ? 128 : 168,
      length: charged ? 0.19 : 0.1,
    });

    // Hand release — the moment the gun stops being yours. Anchors the event in time.
    this.transient(bus, t0, { level: charged ? 0.85 : 0.6, hp: 1400, tailMs: 3.0, grit: 0.5, decay: 0.011 });
    // The frame leaving the palm: a short scrape of metal against glove.
    this.metallicSnap(bus, t0, { hz: charged ? 1650 : 2050, level: charged ? 0.5 : 0.34, bright: 0.7, tail: 0.09 });
    this.cloth(bus, t0, charged ? 0.7 : 0.45);

    // THE ARM. A hard, wide sweep through the throw's arc — this is the layer that says the
    // object was accelerated rather than dropped.
    {
      const src = this.noiseBurst('white', t0, 0.34, this.j(0.1));
      const bp = biquad(ctx, 'bandpass', 900 * this.j(0.08), charged ? 1.5 : 1.1);
      sweep(bp.frequency, t0, charged ? 420 : 640, charged ? 2600 : 1700, 0.115);
      const crunch = shaper(ctx, this.curveCrunch, '2x');
      const g = gain(ctx, 0);
      snapEnv(g.gain, t0, 0.19, charged ? 1.05 : 0.72);
      src.connect(bp);
      bp.connect(crunch);
      crunch.connect(g);
      g.connect(bus);
    }

    // The effort behind the throw. A charged pass is a shoulder, not a wrist.
    this.sub(bus, t0, {
      from: charged ? 200 : 140,
      to: charged ? 48 : 68,
      sweep: 0.09,
      decay: charged ? 0.26 : 0.13,
      level: charged ? 1.0 : 0.55,
      knock: 0.65,
      shape: 0.8,
    });

    // THE LAUNCH. A rising blade tone that accelerates away and hands the object off to the
    // sustained whoosh. Charged passes get a wider, longer sweep because they travel further.
    {
      const from = charged ? 130 : 170;
      const to = charged ? 620 : 430;
      const len = charged ? 0.26 : 0.17;
      const o = osc(ctx, 'sawtooth', from);
      pitchEnv(o.frequency, t0, from, to, len);
      const bp = biquad(ctx, 'bandpass', from * 2, 4.5);
      sweep(bp.frequency, t0, from * 2.2, to * 2.4, len);
      const g = gain(ctx, 0);
      // Swells rather than decays: the object is getting FASTER as it leaves.
      swellEnv(g.gain, t0, len * 0.7, charged ? 0.42 : 0.24, len * 0.5);
      o.connect(bp);
      bp.connect(g);
      g.connect(bus);
      play(o, t0, t0 + len + 0.3);
    }

    return charged ? 0.8 : 0.6;
  }

  /**
   * THE FLIGHT. The sustained whoosh of a gun crossing the arena.
   *
   * This is the sound the lead asked for by name — "more like a WHOOSH, like it's flying
   * through the air" — and the reason the previous version failed is that it was one band
   * of hissing noise at a level that peaked around -22 dBFS. A gun is airborne for a second
   * or more, and every one of those frames has to be audible and, more importantly,
   * **trackable**: an enemy must be able to hear a pass go over their head and know to look
   * up. That is a localisation problem, and localisation needs a broad spectrum, a pitched
   * component and real amplitude movement. So there are four layers:
   *
   *  MASS   Low bandpassed noise, 160–900 Hz. The column of air the object is pushing. This
   *         is the layer that gives the flight a size, and it is the one that survives a
   *         laptop speaker.
   *  EDGE   High bandpassed noise, 1.2–7 kHz, tight Q. The hiss of a hard edge cutting air.
   *         The ear localises almost entirely from this band, so it is what tells a player
   *         WHERE the gun is. It opens up as the gun gets close.
   *  BLADE  A resonant sawtooth. A tumbling object with flat faces makes a pitched tone, and
   *         a pitched tone is what makes the flight followable rather than just present —
   *         you can hear it rise and fall through the throw the way you hear a passing car.
   *  TUMBLE An LFO at roughly twice the gun's spin rate, modulating amplitude AND the mass
   *         band's centre. The gun presents a flat face to the airflow twice per revolution,
   *         and that chop is the single most recognisable feature of a thrown object.
   *
   * Doppler is applied by hand — the Web Audio PannerNode dropped its doppler support years
   * ago — and it is applied to every frequency-domain parameter at once, not just to one, so
   * a pass-by shifts the *whole* sound rather than making one band go slightly sharp.
   */
  throwWhoosh(): WhooshVoice {
    const ctx = this.ctx;
    const t0 = this.now;
    const out = gain(ctx, 0);
    const level = gain(ctx, 0);
    level.connect(out);

    // Tumble rides its own node so it modulates around unity instead of fighting the level
    // automation — the same discipline the hum's tremolo uses.
    const tumbleGain = gain(ctx, 1);
    const tumble = makeLfo(ctx, 7, 0.34, 'sine');
    tumble.connectTo(tumbleGain.gain);
    tumbleGain.connect(level);

    // --- MASS. The air the object displaces.
    const massSrc = this.noise.loop('brown', 1);
    const massBp = biquad(ctx, 'bandpass', 320, 1.1);
    const massSat = shaper(ctx, this.curveWarm, '2x');
    const massG = gain(ctx, 0.9);
    massSrc.connect(massBp);
    massBp.connect(massSat);
    massSat.connect(massG);
    massG.connect(tumbleGain);
    // The tumble also chops the mass band's centre, which reads as the object rotating
    // rather than as a tremolo pedal on a hiss.
    const massWobble = makeLfo(ctx, 7, 90, 'sine');
    massWobble.connectTo(massBp.frequency);

    // --- EDGE. The band the ear localises from.
    const edgeSrc = this.noise.loop('white', 1);
    const edgeBp = biquad(ctx, 'bandpass', 2400, 2.2);
    const edgeLp = biquad(ctx, 'lowpass', 7000, 0.7);
    const edgeG = gain(ctx, 0.34);
    edgeSrc.connect(edgeBp);
    edgeBp.connect(edgeLp);
    edgeLp.connect(edgeG);
    edgeG.connect(tumbleGain);

    // --- BLADE. The pitched component that makes the flight followable.
    const blade = osc(ctx, 'sawtooth', 210);
    const bladeBp = biquad(ctx, 'bandpass', 420, 7.0);
    const bladeG = gain(ctx, 0.16);
    blade.connect(bladeBp);
    bladeBp.connect(bladeG);
    bladeG.connect(tumbleGain);
    // A slow flutter on the blade's pitch: an object tumbling through air does not hold a
    // steady tone, and a steady one would read as a synthesiser immediately.
    const bladeWarble = makeLfo(ctx, 3.4, 14, 'triangle');
    bladeWarble.connectTo(blade.detune);

    const start = t0 + 0.002;
    startAtRandomPhase(massSrc, start, this.rng);
    startAtRandomPhase(edgeSrc, start, this.rng);
    try {
      blade.start(start);
    } catch {
      /* already started */
    }
    for (const l of [tumble, massWobble, bladeWarble]) l.start(start);

    // Fade in over 45 ms. A sustained voice that snaps on at full level announces itself as
    // a voice rather than as an object, and the gun is already moving when this begins.
    out.gain.setValueAtTime(MIN_GAIN, start);
    out.gain.exponentialRampToValueAtTime(1, start + 0.045);

    let stopped = false;
    let speed01 = 0;
    let doppler = 1;
    let proximity = 0;

    const apply = () => {
      const now = ctx.currentTime;
      const s = clamp01(speed01);
      const d = clamp(doppler, 0.5, 2.0);
      const near = clamp01(proximity);
      // Curve, not a square. `s*s` meant a gun at two thirds speed — most of a throw's
      // flight, once drag has bitten — was at 44% level, which is how a one-second event
      // became inaudible three tenths of the way in. A 0.65 power holds the middle of the
      // arc up (half speed is still two thirds level) while keeping the floor low enough
      // that a gun tumbling to rest fades out instead of droning.
      const drive = 0.12 + 0.9 * Math.pow(s, 0.65);

      level.gain.setTargetAtTime(drive * (0.85 + 0.5 * near), now, 0.04);

      // Everything in the frequency domain moves with the doppler together.
      massBp.frequency.setTargetAtTime(clamp((170 + 620 * s) * d, 70, 3000), now, 0.035);
      massBp.Q.setTargetAtTime(0.8 + 0.9 * s, now, 0.06);
      massWobble.depth.gain.setTargetAtTime(50 + 190 * s, now, 0.06);

      edgeBp.frequency.setTargetAtTime(clamp((1200 + 4200 * s) * d, 400, 12000), now, 0.035);
      edgeLp.frequency.setTargetAtTime(clamp((3000 + 6000 * s) * d * (1 + 0.5 * near), 800, 18000), now, 0.05);
      // Close passes are bright and sibilant; distant ones are a low rush. This is the cue
      // that says "that went past MY head".
      edgeG.gain.setTargetAtTime(0.2 + 0.34 * s + 0.4 * near, now, 0.05);

      massSrc.playbackRate.setTargetAtTime(clamp((0.7 + 0.7 * s) * d, 0.35, 2.6), now, 0.04);
      edgeSrc.playbackRate.setTargetAtTime(clamp((0.8 + 0.8 * s) * d, 0.35, 2.8), now, 0.04);

      const bladeHz = clamp((150 + 430 * s) * d, 60, 1800);
      blade.frequency.setTargetAtTime(bladeHz, now, 0.04);
      bladeBp.frequency.setTargetAtTime(bladeHz * 2, now, 0.045);
      bladeG.gain.setTargetAtTime(0.09 + 0.16 * s + 0.14 * near, now, 0.05);

      // Spin rate. THROW_SPIN_RATE is 22 rad/s ≈ 3.5 rev/s, and a flat-faced object chops
      // the airflow twice per revolution — so ~7 Hz at full speed, slowing as it decelerates.
      tumble.oscillator.frequency.setTargetAtTime(2.5 + 6 * s, now, 0.08);
      massWobble.oscillator.frequency.setTargetAtTime(2.5 + 6 * s, now, 0.08);
      tumble.depth.gain.setTargetAtTime(0.16 + 0.3 * s, now, 0.08);
    };

    return {
      output: out,
      setSpeed(v: number) {
        if (stopped) return;
        speed01 = v;
        apply();
      },
      setDoppler(r: number) {
        if (stopped) return;
        doppler = r;
        apply();
      },
      setProximity(v: number) {
        if (stopped) return;
        proximity = v;
        apply();
      },
      /**
       * The flight ending. Longer than a normal voice teardown on purpose: a whoosh that is
       * cut dead the instant the gun is caught makes the catch feel like an edit. The air
       * has to keep going for a moment after the object stops.
       */
      stop(fadeSec = 0.16) {
        if (stopped) return;
        stopped = true;
        const now = ctx.currentTime;
        // The tail darkens as it dies — air running out, not a fader being pulled.
        edgeLp.frequency.cancelScheduledValues(now);
        edgeLp.frequency.setTargetAtTime(900, now, fadeSec * 0.5);
        bladeG.gain.setTargetAtTime(0, now, fadeSec * 0.35);
        const end = fadeOut(level.gain, now, fadeSec);
        stopSafe(massSrc, end + 0.05);
        stopSafe(edgeSrc, end + 0.05);
        stopSafe(blade, end + 0.05);
        for (const l of [tumble, massWobble, bladeWarble]) l.stop(end + 0.05);
        setTimeout(
          () => {
            try {
              out.disconnect();
            } catch {
              /* already gone */
            }
          },
          (fadeSec + 0.3) * 1000,
        );
      },
    };
  }

  /** The gun striking geometry. Loudness and brightness scale with impact speed. */
  gunBounce(t: SfxTarget, speed: number, surface: SurfaceKind): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const v = SURFACES[surface] ?? SURFACES[SurfaceKind.Concrete];
    const hard = clamp01(speed / 22);
    const bus = gain(ctx, 0.5 + 0.72 * hard);
    this.out(bus, t, 0.5 + 0.4 * hard, t0);

    this.metallicSnap(bus, t0, {
      hz: lerpN(680, 1500, hard) * lerpN(1, 1.5, v.ring),
      level: 0.55 + 0.55 * hard,
      bright: clamp01(0.25 + 0.55 * hard + 0.2 * v.ring),
      tail: 0.12 + 0.28 * v.decay * hard,
    });

    // A dropped gun is a dense object, not a ping. The sub is what gives it a mass.
    this.sub(bus, t0, {
      from: v.thump * 1.9,
      to: v.thump * 0.5,
      sweep: 0.05,
      decay: 0.11 * v.decay,
      level: 0.42 + 0.5 * hard,
      knock: 0.55,
    });

    return 0.6;
  }

  /**
   * A gun teleporting back to a fair spawn. A synthetic re-materialise that lands on a real
   * physical arrival — the sweep alone was pure sci-fi whoosh with nothing at the end of it.
   *
   * The two reasons sound different on purpose. `void` means it fell out of the world and is
   * being dragged back: darker, lower, a recall. `abandoned` means it sat untouched too long
   * and reset itself: brighter and cleaner, an administrative event, not a loss.
   */
  gunReset(t: SfxTarget, reason: 'void' | 'abandoned' = 'abandoned'): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const voided = reason === 'void';
    const bus = gain(ctx, 0.75);
    this.out(bus, t, 0.6, t0);

    // The charge-up.
    const o = osc(ctx, 'sine', 220);
    pitchEnv(o.frequency, t0, voided ? 60 : 110, voided ? 420 : 700, 0.3);
    const g = gain(ctx, 0);
    applyADSR(g.gain, t0, { a: 0.09, d: 0.14, s: 0.3, r: 0.2 }, 0.24, 0.34);
    const rm = makeRingMod(ctx, voided ? 43 : 71);
    o.connect(rm.input);
    rm.output.connect(g);
    g.connect(bus);
    play(o, t0, t0 + 0.7);
    rm.start(t0);
    rm.stop(t0 + 0.7);

    const n = this.noiseBurst('white', t0, 0.35, this.j(0.1));
    const bp = biquad(ctx, 'bandpass', 2000, 2.5);
    sweep(bp.frequency, t0, 800, voided ? 3400 : 5200, 0.28);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.05, 0.24, 0.2);
    n.connect(bp);
    bp.connect(ng);
    ng.connect(bus);

    // ARRIVAL. The sweep now resolves into an actual object appearing in the room, so the
    // event has a moment rather than just a gesture.
    const land = t0 + 0.3;
    this.transient(bus, land, { level: 0.7, hp: 1600, tailMs: 2.6, grit: 0.4, decay: 0.01 });
    this.metallicSnap(bus, land, { hz: voided ? 940 : 1420, level: 0.7, bright: voided ? 0.5 : 0.8, tail: 0.3 });
    this.sub(bus, land, { from: voided ? 150 : 190, to: voided ? 44 : 66, sweep: 0.06, decay: 0.24, level: 0.85, knock: 0.6 });

    return 0.95;
  }

  // -------------------------------------------------------------------------
  // MOVEMENT
  // -------------------------------------------------------------------------

  /**
   * A footstep. Five layers, same as everything else: boot transient, heel sub, surface
   * scuff, material ring, and cloth. A footstep is small, but small is not the same as thin —
   * a step with no transient and no low end is the sound that makes a game feel weightless.
   */
  footstep(t: SfxTarget, surface: SurfaceKind, intensity: number, crouched: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const v = SURFACES[surface] ?? SURFACES[SurfaceKind.Concrete];
    const amp = clamp(intensity, 0.15, 1.3) * v.level * (crouched ? 0.5 : 1);
    const bus = gain(ctx, 0.62 * amp);
    this.out(bus, t, 0.4, t0);

    // Boot leather meeting the floor — 0.4 ms. This is what makes a step read as contact.
    this.transient(bus, t0, { level: 0.5, hp: v.scuff * 0.35, lp: 9000, tailMs: 3.0, grit: 0.6, decay: 0.01 });

    // Heel — the low half of the step, now saturated with a knock so it exists at all on a
    // laptop speaker instead of being a purely theoretical 90 Hz sine.
    this.sub(bus, t0, {
      from: v.thump * 1.6,
      to: v.thump * 0.62,
      sweep: 0.032,
      decay: 0.068 * v.decay,
      level: 0.85,
      knock: 0.5,
      light: true,
    });

    // Scuff — the surface's own texture.
    const n = this.noiseBurst(surface === SurfaceKind.Glass ? 'white' : 'pink', t0, 0.14, randRange(this.rng, 0.9, 1.15));
    const bp = biquad(ctx, 'bandpass', v.scuff * randRange(this.rng, 0.88, 1.14), v.q);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0007, 0.05 * v.decay, 0.62);
    n.connect(bp);
    bp.connect(ng);
    ng.connect(bus);

    // Ring — metal, grate and glass sing; concrete does not.
    if (v.ring > 0.05) {
      const ping = this.metal.source({
        freq: v.ringHz * randRange(this.rng, 0.9, 1.12),
        seconds: 0.28 * v.decay,
        sustain: 0.3,
        brightness: 0.75,
        metal: 0.9,
        variant: (this.rng() * 4) | 0,
      });
      const pg = gain(ctx, 0);
      percEnv(pg.gain, t0 + 0.004, 0.001, 0.2 * v.decay, 0.32 * v.ring);
      ping.connect(pg);
      pg.connect(bus);
      play(ping, t0 + 0.004, t0 + 0.4);
    }

    // Gear. A person running is fabric and webbing as much as it is boots, and this is the
    // layer that stops a footstep sounding like a drum machine.
    this.cloth(bus, t0 + 0.012 * this.j(0.4), crouched ? 0.14 : 0.24 * amp);

    return 0.4;
  }

  /**
   * Gun-handling foley for a player who is carrying. Emitted between footsteps, so a
   * moving armed player is continuously audible as a person with a heavy object — which is
   * also intel, exactly like the hum.
   */
  gunFoley(t: SfxTarget, chambered: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 0.7);
    this.out(bus, t, 0.3, t0);

    // Sling webbing and a glove shifting on the frame.
    this.cloth(bus, t0, 0.5);

    // A small metal component moving against another. Two or three, unevenly spaced.
    const taps = 2 + ((this.rng() * 2) | 0);
    for (let i = 0; i < taps; i++) {
      const at = t0 + i * randRange(this.rng, 0.028, 0.075);
      const ping = this.metal.source({
        freq: randRange(this.rng, 1500, 3400),
        seconds: 0.09,
        sustain: 0.12,
        brightness: 0.85,
        metal: 0.95,
        variant: (this.rng() * 4) | 0,
      });
      const hp = biquad(ctx, 'highpass', 1600, 0.7);
      const g = gain(ctx, 0);
      percEnv(g.gain, at, 0.0004, 0.04, randRange(this.rng, 0.06, 0.15));
      ping.connect(hp);
      hp.connect(g);
      g.connect(bus);
      play(ping, at, at + 0.14);
    }

    // A chambered gun has a round in it, and a round has mass that shifts.
    if (chambered) {
      this.sub(bus, t0, { from: 120, to: 58, sweep: 0.04, decay: 0.06, level: 0.16, knock: 0.35, light: true });
    }

    return 0.3;
  }

  land(t: SfxTarget, speed: number, surface: SurfaceKind): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const v = SURFACES[surface] ?? SURFACES[SurfaceKind.Concrete];
    const hard = clamp01(speed / 20);
    const bus = gain(ctx, 0.55 + 0.85 * hard);
    this.out(bus, t, 0.5, t0);

    // The slam. A landing is the second-heaviest impact in the game after the gunshot, and
    // it was previously the softest thing in the mix — no transient at all.
    this.transient(bus, t0, { level: 0.45 + 0.55 * hard, hp: 400, lp: 6000, tailMs: 4.5, grit: 0.7, decay: 0.016 });

    this.sub(bus, t0, {
      from: v.thump * 2.2,
      to: v.thump * 0.42,
      sweep: 0.055,
      decay: (0.14 + 0.12 * hard) * v.decay,
      level: 1.0,
      knock: 0.65,
      shape: 0.8,
    });

    const n = this.noiseBurst('brown', t0, 0.22, this.j(0.1));
    const lp = biquad(ctx, 'lowpass', lerpN(700, 2400, hard), 1.1);
    const crunch = shaper(ctx, this.curveCrunch, '2x');
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0008, 0.1 + 0.1 * hard, 0.68);
    n.connect(lp);
    lp.connect(crunch);
    crunch.connect(ng);
    ng.connect(bus);

    if (v.ring > 0.05 && hard > 0.2) {
      const ping = this.metal.source({
        freq: v.ringHz * 0.8 * this.j(0.06),
        seconds: 0.5,
        sustain: 0.5,
        brightness: 0.7,
        metal: 0.9,
        variant: (this.rng() * 3) | 0,
      });
      const pg = gain(ctx, 0);
      percEnv(pg.gain, t0, 0.001, 0.36, 0.36 * v.ring * hard);
      ping.connect(pg);
      pg.connect(bus);
      play(ping, t0, t0 + 0.7);
    }

    // Gear settling after the impact — the give-away that a body landed, not a crate.
    this.cloth(bus, t0 + 0.02, 0.2 + 0.3 * hard);

    return 0.8;
  }

  jump(t: SfxTarget, double: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 0.72);
    this.out(bus, t, 0.28, t0);

    // The push-off is an impact too: the boot loading against the floor.
    this.transient(bus, t0, { level: double ? 0.4 : 0.55, hp: 900, lp: 7000, tailMs: 3.0, grit: 0.6, decay: 0.009 });

    const n = this.noiseBurst('pink', t0, 0.2, this.j(0.12));
    const bp = biquad(ctx, 'bandpass', 700 * this.j(0.1), 1.0);
    sweep(bp.frequency, t0, 420, 1300, 0.105);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0015, 0.1, 0.48);
    n.connect(bp);
    bp.connect(ng);
    ng.connect(bus);

    this.cloth(bus, t0, 0.3);

    if (double) {
      // A synthetic mid-air pop so the second jump reads as an ability, not a step.
      const o = osc(ctx, 'triangle', 300);
      pitchEnv(o.frequency, t0, 260 * this.j(0.03), 900, 0.1);
      const g = gain(ctx, 0);
      percEnv(g.gain, t0, 0.0015, 0.13, 0.42);
      const fold = shaper(ctx, this.curveFold, '2x');
      o.connect(fold);
      fold.connect(g);
      g.connect(bus);
      play(o, t0, t0 + 0.3);
      // An exo-servo kicking. Mechanical detail on a traversal verb, per DESIGN.md §5.6.
      this.sub(bus, t0, { from: 190, to: 96, sweep: 0.06, decay: 0.1, level: 0.5, knock: 0.5 });
    } else {
      this.sub(bus, t0, { from: 165, to: 66, sweep: 0.045, decay: 0.085, level: 0.6, knock: 0.5 });
    }

    return 0.4;
  }

  /** The moment a slide starts — a hard scuff before the sustained scrape takes over. */
  slideStart(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 0.85);
    this.out(bus, t, 0.42, t0);

    // The knee-drop. A slide begins with a body hitting the floor, and the old version had
    // a 6 ms attack — soft enough that the slide seemed to start from nothing.
    this.transient(bus, t0, { level: 0.6, hp: 500, lp: 6000, tailMs: 4.0, grit: 0.7, decay: 0.014 });

    const n = this.noiseBurst('white', t0, 0.3, this.j(0.1));
    const bp = biquad(ctx, 'bandpass', 2600 * this.j(0.1), 1.4);
    sweep(bp.frequency, t0, 4200, 1200, 0.2);
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.0015, 0.2, 0.58);
    n.connect(bp);
    bp.connect(g);
    g.connect(bus);

    this.sub(bus, t0, { from: 190, to: 62, sweep: 0.07, decay: 0.16, level: 0.6, knock: 0.55 });
    this.cloth(bus, t0, 0.4);

    return 0.6;
  }

  /**
   * The sustained scrape of a slide or a wall-run. Velvet noise through a resonant bandpass —
   * velvet has no hiss floor between its impulses, so it grinds instead of shushing.
   */
  scrape(kind: 'slide' | 'wallrun'): ScrapeVoice {
    const ctx = this.ctx;
    const t0 = this.now;
    const out = gain(ctx, 0);

    const src = this.noise.loop('velvet', 1);
    const bp = biquad(ctx, 'bandpass', kind === 'slide' ? 1700 : 2600, kind === 'slide' ? 1.2 : 2.4);
    const lp = biquad(ctx, 'lowpass', 5200, 0.7);
    const level = gain(ctx, 0);
    const drive = shaper(ctx, this.curveWarm, '2x');

    // A quiet resonant partial gives the wall-run its metallic edge.
    const ring = biquad(ctx, 'peaking', kind === 'slide' ? 320 : 1450, 4.5, kind === 'slide' ? 5 : 9);

    src.connect(bp);
    bp.connect(ring);
    ring.connect(lp);
    lp.connect(drive);
    drive.connect(level);
    level.connect(out);

    const grind = makeLfo(ctx, kind === 'slide' ? 11 : 19, 260, 'triangle');
    grind.connectTo(bp.frequency);

    startAtRandomPhase(src, t0 + 0.002, this.rng);
    grind.start(t0 + 0.002);

    let stopped = false;
    let intensity = 0;
    let voicing = SURFACES[SurfaceKind.Concrete];

    const apply = () => {
      const now = ctx.currentTime;
      const i = clamp01(intensity);
      level.gain.setTargetAtTime(0.015 + 0.24 * i, now, 0.07);
      bp.frequency.setTargetAtTime(clamp(voicing.scuff * (0.5 + 0.7 * i), 420, 9000), now, 0.08);
      lp.frequency.setTargetAtTime(clamp(2200 + 6000 * i, 800, 15000), now, 0.09);
      src.playbackRate.setTargetAtTime(clamp(0.7 + 0.8 * i, 0.4, 2.0), now, 0.08);
    };

    return {
      output: out,
      setIntensity(v: number) {
        if (stopped) return;
        intensity = v;
        apply();
      },
      setSurface(s: SurfaceKind) {
        if (stopped) return;
        voicing = SURFACES[s] ?? SURFACES[SurfaceKind.Concrete];
        ring.frequency.setTargetAtTime(clamp(voicing.ringHz || 320, 80, 8000), ctx.currentTime, 0.2);
        apply();
      },
      stop(fadeSec = 0.12) {
        if (stopped) return;
        stopped = true;
        const now = ctx.currentTime;
        const end = fadeOut(level.gain, now, fadeSec);
        stopSafe(src, end + 0.05);
        grind.stop(end + 0.05);
        setTimeout(
          () => {
            try {
              out.disconnect();
            } catch {
              /* already gone */
            }
          },
          (fadeSec + 0.25) * 1000,
        );
      },
    };
  }

  wallrunStart(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 0.8);
    this.out(bus, t, 0.42, t0);

    // Boot slapping onto vertical concrete. Attack was 5 ms; a wall-run attaching is a
    // moment, and a moment needs an edge.
    this.transient(bus, t0, { level: 0.6, hp: 800, lp: 8000, tailMs: 3.2, grit: 0.65, decay: 0.011 });

    const n = this.noiseBurst('white', t0, 0.24, this.j(0.1));
    const bp = biquad(ctx, 'bandpass', 3000 * this.j(0.1), 2.0);
    sweep(bp.frequency, t0, 1400, 3800, 0.14);
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.0015, 0.16, 0.52);
    n.connect(bp);
    bp.connect(g);
    g.connect(bus);

    const ping = this.metal.source({
      freq: 940 * this.j(0.05),
      seconds: 0.4,
      sustain: 0.45,
      brightness: 0.6,
      metal: 0.85,
      variant: (this.rng() * 3) | 0,
    });
    const pg = gain(ctx, 0);
    percEnv(pg.gain, t0, 0.002, 0.3, 0.24);
    ping.connect(pg);
    pg.connect(bus);
    play(ping, t0, t0 + 0.6);

    this.sub(bus, t0, { from: 175, to: 72, sweep: 0.05, decay: 0.1, level: 0.5, knock: 0.5 });
    this.cloth(bus, t0, 0.3);

    return 0.6;
  }

  /**
   * Letting go of a wall without jumping — the run simply expiring. This event existed in
   * the sim and produced nothing at all; a traversal verb that ends silently makes the
   * movement system feel like it is guessing.
   */
  wallrunEnd(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 0.5);
    this.out(bus, t, 0.35, t0);

    // The scrape releasing: a short descending sigh of contact noise.
    const n = this.noiseBurst('velvet', t0, 0.16, this.j(0.12));
    const bp = biquad(ctx, 'bandpass', 2400 * this.j(0.1), 2.2);
    sweep(bp.frequency, t0, 2800, 900, 0.13);
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.002, 0.12, 0.42);
    n.connect(bp);
    bp.connect(g);
    g.connect(bus);

    this.cloth(bus, t0, 0.26);
    this.sub(bus, t0, { from: 120, to: 62, sweep: 0.05, decay: 0.07, level: 0.24, knock: 0.4 });

    return 0.35;
  }

  wallJump(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1.0);
    this.out(bus, t, 0.42, t0);

    this.transient(bus, t0, { level: 0.75, hp: 700, lp: 7000, tailMs: 3.4, grit: 0.65, decay: 0.012 });

    const n = this.noiseBurst('white', t0, 0.2, this.j(0.12));
    const bp = biquad(ctx, 'bandpass', 1800 * this.j(0.1), 1.1);
    sweep(bp.frequency, t0, 3600, 700, 0.115);
    const crunch = shaper(ctx, this.curveCrunch, '2x');
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.0008, 0.13, 0.68);
    n.connect(bp);
    bp.connect(crunch);
    crunch.connect(g);
    g.connect(bus);

    // The kick off the wall. This is a strong push and it should be felt.
    this.sub(bus, t0, { from: 245, to: 58, sweep: 0.085, decay: 0.16, level: 0.85, knock: 0.6, shape: 0.8 });
    this.cloth(bus, t0, 0.32);

    return 0.6;
  }

  /**
   * Dash. A whoomph: a compressed burst of air, a sub thump, and a reverse-swelling tail
   * that makes it feel like the world snapped past you rather than that you moved.
   */
  dash(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, t.local ? 1.2 : 0.85);
    this.out(bus, t, 0.38, t0);

    // The kick. A dash is instantaneous in the sim and it must be instantaneous in the mix.
    this.transient(bus, t0, { level: 0.62, hp: 1100, lp: 9000, tailMs: 2.6, grit: 0.5, decay: 0.009 });

    // Air.
    const n = this.noiseBurst('white', t0, 0.36, this.j(0.1));
    const bp = biquad(ctx, 'bandpass', 1200 * this.j(0.08), 0.9);
    sweep(bp.frequency, t0, 2600, 380, 0.2);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0015, 0.22, 0.7);
    n.connect(bp);
    bp.connect(ng);
    ng.connect(bus);

    // Sub whump.
    this.sub(bus, t0, { from: 158, to: 42, sweep: 0.12, decay: 0.24, level: 1.0, knock: 0.6, shape: 0.85 });

    // Servo — the mechanical half of the verb. A short pitched whine under the air burst
    // makes the dash read as equipment firing rather than as a whoosh sample.
    {
      const o = osc(ctx, 'sawtooth', 620 * this.j(0.04));
      pitchEnv(o.frequency, t0, 420, 1350, 0.085);
      const sbp = biquad(ctx, 'bandpass', 900, 5.0);
      sweep(sbp.frequency, t0, 700, 2200, 0.1);
      const g = gain(ctx, 0);
      percEnv(g.gain, t0, 0.0012, 0.09, 0.22);
      o.connect(sbp);
      sbp.connect(g);
      g.connect(bus);
      play(o, t0, t0 + 0.22);
    }

    // Swell into the hit — a rising ramp of filtered noise ahead of the impact point.
    const s = this.noiseBurst('pink', t0, 0.14, this.j(0.15));
    const shp = biquad(ctx, 'highpass', 1600, 0.8);
    const sg = gain(ctx, 0);
    sg.gain.setValueAtTime(MIN_GAIN, t0);
    sg.gain.exponentialRampToValueAtTime(0.38, t0 + 0.075);
    sg.gain.exponentialRampToValueAtTime(MIN_GAIN, t0 + 0.13);
    sg.gain.setValueAtTime(0, t0 + 0.14);
    s.connect(shp);
    shp.connect(sg);
    sg.connect(bus);

    this.cloth(bus, t0, 0.3);

    return 0.7;
  }

  // -------------------------------------------------------------------------
  // GRAPPLE
  // -------------------------------------------------------------------------

  grappleFire(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1.0);
    this.out(bus, t, 0.38, t0);

    // Pneumatic launch.
    const n = this.noiseBurst('white', t0, 0.2, this.j(0.1));
    const bp = biquad(ctx, 'bandpass', 2400 * this.j(0.08), 1.3);
    sweep(bp.frequency, t0, 3600, 900, 0.1);
    const crunch = shaper(ctx, this.curveCrunch, '2x');
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0006, 0.12, 0.7);
    n.connect(bp);
    bp.connect(crunch);
    crunch.connect(ng);
    ng.connect(bus);

    // Mechanism — the launcher firing.
    this.metallicSnap(bus, t0, { hz: 1450, level: 0.7, bright: 0.7, tail: 0.14 });
    // Gas charge. Small, but it puts a body under what was a pure hiss.
    this.sub(bus, t0, { from: 160, to: 68, sweep: 0.045, decay: 0.11, level: 0.5, knock: 0.55 });

    // The hook leaving, rising as it flies out.
    const o = osc(ctx, 'sawtooth', 220);
    pitchEnv(o.frequency, t0, 180, 620 * this.j(0.04), 0.24);
    const lp = biquad(ctx, 'lowpass', 2200, 4.0);
    sweep(lp.frequency, t0, 900, 3400, 0.24);
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.002, 0.24, 0.26);
    o.connect(lp);
    lp.connect(g);
    g.connect(bus);
    play(o, t0, t0 + 0.5);

    return 0.7;
  }

  grappleAttach(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1.05);
    this.out(bus, t, 0.6, t0);

    // Steel biting steel.
    this.transient(bus, t0, { level: 0.8, hp: 1200, tailMs: 3.0, grit: 0.45, decay: 0.01 });
    this.metallicSnap(bus, t0, { hz: 820, level: 1.0, bright: 0.55, tail: 0.42 });
    this.metallicSnap(bus, t0 + 0.018 * this.j(0.15), { hz: 1240, level: 0.5, bright: 0.75, tail: 0.26 });

    // The anchor taking the load. This is a hard mechanical arrest and it needs the weight.
    this.sub(bus, t0, { from: 210, to: 58, sweep: 0.06, decay: 0.2, level: 0.8, knock: 0.6 });

    return 0.8;
  }

  /** The tether under load. Pitch and brightness climb with tension — you can hear the apex. */
  tether(): TetherVoice {
    const ctx = this.ctx;
    const t0 = this.now;
    const out = gain(ctx, 0);

    const o1 = osc(ctx, 'sawtooth', 180, -8);
    const o2 = osc(ctx, 'sawtooth', 180, +9);
    const bp = biquad(ctx, 'bandpass', 700, 3.4);
    const lp = biquad(ctx, 'lowpass', 2600, 0.8);
    const level = gain(ctx, 0);

    // Creak: filtered velvet noise that gets louder as the cable strains.
    const creak = this.noise.loop('velvet', 0.8);
    const creakBp = biquad(ctx, 'bandpass', 2400, 5.0);
    const creakG = gain(ctx, 0);

    o1.connect(bp);
    o2.connect(bp);
    bp.connect(lp);
    lp.connect(level);
    creak.connect(creakBp);
    creakBp.connect(creakG);
    creakG.connect(level);
    level.connect(out);

    const wobble = makeLfo(ctx, 5.5, 9, 'sine');
    wobble.connectTo(o1.detune);

    try {
      o1.start(t0 + 0.002);
      o2.start(t0 + 0.002);
    } catch {
      /* already started */
    }
    startAtRandomPhase(creak, t0 + 0.002, this.rng);
    wobble.start(t0 + 0.002);

    let stopped = false;

    return {
      output: out,
      setTension(v: number) {
        if (stopped) return;
        const tension = clamp01(v);
        const now = ctx.currentTime;
        const hz = lerpN(120, 330, tension * tension);
        o1.frequency.setTargetAtTime(hz, now, 0.06);
        o2.frequency.setTargetAtTime(hz * 1.005, now, 0.06);
        bp.frequency.setTargetAtTime(lerpN(420, 1900, tension), now, 0.07);
        lp.frequency.setTargetAtTime(lerpN(1400, 5200, tension), now, 0.08);
        level.gain.setTargetAtTime(0.03 + 0.13 * tension, now, 0.06);
        creakG.gain.setTargetAtTime(0.05 + 0.3 * tension * tension, now, 0.07);
        wobble.oscillator.frequency.setTargetAtTime(3 + 9 * tension, now, 0.1);
      },
      stop(fadeSec = 0.1) {
        if (stopped) return;
        stopped = true;
        const now = ctx.currentTime;
        const end = fadeOut(level.gain, now, fadeSec);
        stopSafe(o1, end + 0.05);
        stopSafe(o2, end + 0.05);
        stopSafe(creak, end + 0.05);
        wobble.stop(end + 0.05);
        setTimeout(
          () => {
            try {
              out.disconnect();
            } catch {
              /* already gone */
            }
          },
          (fadeSec + 0.25) * 1000,
        );
      },
    };
  }

  grappleRelease(t: SfxTarget, boosted: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, boosted ? 1.25 : 0.8);
    this.out(bus, t, 0.38, t0);

    // The cable letting go.
    this.transient(bus, t0, { level: 0.62, hp: 1300, tailMs: 4.0, grit: 0.45, decay: 0.014 });

    const n = this.noiseBurst('white', t0, 0.22, this.j(0.1));
    const bp = biquad(ctx, 'bandpass', 2200 * this.j(0.08), 1.4);
    sweep(bp.frequency, t0, 3400, 700, 0.16);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0008, 0.15, 0.52);
    n.connect(bp);
    bp.connect(ng);
    ng.connect(bus);

    // The tether's stored energy leaving. Bigger when you nailed the apex.
    this.sub(bus, t0, {
      from: boosted ? 200 : 140,
      to: boosted ? 52 : 74,
      sweep: 0.08,
      decay: boosted ? 0.2 : 0.12,
      level: boosted ? 0.8 : 0.42,
      knock: 0.55,
    });

    if (boosted) {
      // Nailed the apex. A rising confirmation that is unmistakably a reward.
      [noteHz(19), noteHz(26), noteHz(31)].forEach((hz, i) => {
        const o = osc(ctx, 'triangle', hz);
        const g = gain(ctx, 0);
        percEnv(g.gain, t0 + 0.02 + i * 0.035, 0.002, 0.16, 0.2);
        o.connect(g);
        g.connect(bus);
        play(o, t0 + 0.02 + i * 0.035, t0 + 0.4 + i * 0.035);
      });
      const sub = osc(ctx, 'sine', 70);
      pitchEnv(sub.frequency, t0, 70, 150, 0.18);
      const sg = gain(ctx, 0);
      percEnv(sg.gain, t0, 0.006, 0.2, 0.5);
      sub.connect(sg);
      sg.connect(bus);
      play(sub, t0, t0 + 0.45);
    }

    return 0.7;
  }

  // -------------------------------------------------------------------------
  // PULSE
  // -------------------------------------------------------------------------

  /**
   * PULSE — a kinetic shove. A low thump that leaves the body, and a rising shimmer of
   * ring-modulated partials expanding outward behind it.
   */
  pulse(t: SfxTarget, connected: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, t.local ? 1.3 : 0.95);
    this.out(bus, t, 0.65, t0);

    // The discharge. PULSE is the unarmed player's only offensive verb — it has to hit.
    this.transient(bus, t0, { level: 0.7, hp: 500, lp: 5000, tailMs: 3.6, grit: 0.55, decay: 0.012 });

    // The thump.
    this.sub(bus, t0, { from: 172, to: 37, sweep: 0.1, decay: 0.3, level: 1.1, knock: 0.7, shape: 0.8 });

    // The shove — a body of noise pushed outward.
    const n = this.noiseBurst('brown', t0, 0.3, this.j(0.1));
    const lp = biquad(ctx, 'lowpass', 800, 1.6);
    sweep(lp.frequency, t0, 1500, 260, 0.2);
    const crunch = shaper(ctx, this.curveCrunch, '2x');
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0015, 0.2, 0.65);
    n.connect(lp);
    lp.connect(crunch);
    crunch.connect(ng);
    ng.connect(bus);

    // The shimmer — four partials sweeping up through a folding waveshaper.
    const rm = makeRingMod(ctx, 143, 'sine');
    const shimmer = gain(ctx, 0);
    percEnv(shimmer.gain, t0 + 0.012, 0.02, 0.34, 0.2);
    const fold = shaper(ctx, this.curveFold, '2x');
    [1, 1.51, 2.02, 3.03].forEach((r, i) => {
      const s = osc(ctx, 'triangle', 420 * r);
      pitchEnv(s.frequency, t0, 300 * r, 1500 * r, 0.3);
      const g = gain(ctx, 0.5 / (i + 1));
      s.connect(g);
      g.connect(rm.input);
      play(s, t0, t0 + 0.55);
    });
    const hp = biquad(ctx, 'highpass', 700, 0.7);
    rm.output.connect(fold);
    fold.connect(hp);
    hp.connect(shimmer);
    shimmer.connect(bus);
    rm.start(t0);
    rm.stop(t0 + 0.6);

    if (connected) {
      // It hit something. A hard, gratifying impact snaps onto the front — and a second sub
      // under it, because connecting with a PULSE is one of the best plays in the game.
      const at = t0 + 0.008;
      this.transient(bus, at, { level: 0.85, hp: 800, lp: 4200, tailMs: 5.0, grit: 0.6, decay: 0.016 });
      this.sub(bus, at, { from: 210, to: 54, sweep: 0.06, decay: 0.18, level: 0.7, knock: 0.65 });
    }

    return 0.9;
  }

  /**
   * A gun knocked out of someone's hands. Harsh, clattering, chaotic.
   *
   * Its job is to be instantly distinguishable from a catch, which is the other sound in
   * the game where a gun changes hands. A catch is ONE mechanism closing cleanly; a disarm
   * is five things happening at once in no particular order. So: a hard blow, then an
   * irregular clatter of four snaps at random pitches and random spacing, then a descending
   * sweep that says the object is now falling away from someone.
   */
  disarm(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1.25);
    this.out(bus, t, 0.6, t0);

    this.transient(bus, t0, { level: 1.0, hp: 1000, tailMs: 3.0, grit: 0.55, decay: 0.011 });
    // The strike itself — a wide, dirty slap. This is a hand being hit, not a latch opening.
    {
      const n = this.noiseBurst('white', t0, 0.14, this.j(0.12));
      const bp = biquad(ctx, 'bandpass', 1100 * this.j(0.1), 0.9);
      sweep(bp.frequency, t0, 2400, 520, 0.09);
      const crunch = shaper(ctx, this.curveCrunch, '2x');
      const g = gain(ctx, 0);
      snapEnv(g.gain, t0, 0.1, 0.85);
      n.connect(bp);
      bp.connect(crunch);
      crunch.connect(g);
      g.connect(bus);
    }

    // Four snaps, unevenly spaced. The unevenness is the whole read: a mechanism is regular
    // and this is emphatically not a mechanism working.
    let at = t0;
    for (let i = 0; i < 4; i++) {
      at += randRange(this.rng, 0.016, 0.058);
      this.metallicSnap(bus, at, {
        hz: randRange(this.rng, 780, 2400),
        level: 0.95 - i * 0.17,
        bright: randRange(this.rng, 0.55, 0.9),
        tail: randRange(this.rng, 0.12, 0.26),
      });
    }

    // The blow that dislodged it.
    this.sub(bus, t0, { from: 220, to: 48, sweep: 0.08, decay: 0.24, level: 0.95, knock: 0.7, shape: 0.8 });

    const o = osc(ctx, 'sawtooth', 300);
    pitchEnv(o.frequency, t0, 340 * this.j(0.04), 90, 0.2);
    const lp = biquad(ctx, 'lowpass', 1400, 5.0);
    sweep(lp.frequency, t0, 2400, 400, 0.22);
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.0015, 0.22, 0.34);
    o.connect(lp);
    lp.connect(g);
    g.connect(bus);
    play(o, t0, t0 + 0.45);

    return 0.8;
  }

  // -------------------------------------------------------------------------
  // FEEDBACK — hit markers, damage, healing
  // -------------------------------------------------------------------------

  /**
   * Hit markers. These are head-locked and completely dry: they are UI, not world.
   *
   * Body is a tight two-tone tick — informative, but it does not celebrate.
   * Head is the dopamine hit: a bright ascending FM triad with a transient on the front and
   * a sub under it. It has to be instantly, unmistakably different from body, because
   * headshots are the whole game.
   */
  hitMarker(t: SfxTarget, head: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, head ? 0.95 : 0.7);
    this.out(bus, t, 0);

    if (!head) {
      // Informative, not celebratory — but it still gets a real transient and a small sub,
      // because a hit marker with neither is a UI beep and reads as "menu", not "combat".
      this.transient(bus, t0, { level: 0.45, hp: 1800, tailMs: 2.0, grit: 0.35, decay: 0.008 });
      [980, 1470].forEach((hz, i) => {
        const o = osc(ctx, 'square', hz);
        const g = gain(ctx, 0);
        percEnv(g.gain, t0 + i * 0.018, 0.0006, 0.032, 0.34 - i * 0.1);
        const bp = biquad(ctx, 'bandpass', hz, 3.0);
        o.connect(bp);
        bp.connect(g);
        g.connect(bus);
        play(o, t0 + i * 0.018, t0 + i * 0.018 + 0.1);
      });
      this.sub(bus, t0, { from: 150, to: 78, sweep: 0.04, decay: 0.075, level: 0.32, knock: 0.5 });
      return 0.3;
    }

    // Transient.
    this.transient(bus, t0, { level: 0.62, hp: 2000, tailMs: 2.5, grit: 0.3, decay: 0.013 });

    // Ascending FM triad: C6, G6, C7. Bell-bright, fast, decisive.
    [noteHz(39), noteHz(46), noteHz(51)].forEach((hz, i) => {
      const at = t0 + i * 0.026;
      const fm = makeFm(ctx, hz, 1.41, hz * 1.6, 'sine', 'sine');
      const g = gain(ctx, 0);
      percEnv(g.gain, at, 0.0008, 0.16 - i * 0.02, 0.38 - i * 0.05);
      percEnv(fm.index.gain, at, 0.0008, 0.06, hz * 2.2);
      fm.output.connect(g);
      g.connect(bus);
      fm.start(at);
      fm.stop(at + 0.35);
    });

    // A short sub so it lands in the body, not only in the ears.
    this.sub(bus, t0, { from: 190, to: 62, sweep: 0.07, decay: 0.16, level: 0.6, knock: 0.6 });

    return 0.5;
  }

  /** Being wounded. A low, sickening drop plus a wet impact. */
  wound(t: SfxTarget, local: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, local ? 1.15 : 0.75);
    this.out(bus, t, local ? 0.24 : 0.55, t0);

    // The hit itself, before the sickening part. Being wounded is the moment the round ends
    // for you if you are not careful, and it has to arrive as an impact, not as a mood.
    this.transient(bus, t0, { level: 0.7, hp: 350, lp: 3200, tailMs: 6.0, grit: 0.8, decay: 0.018 });
    this.sub(bus, t0, { from: 185, to: 46, sweep: 0.09, decay: 0.3, level: local ? 0.9 : 0.6, knock: 0.6 });

    const o = osc(ctx, 'sawtooth', 240);
    pitchEnv(o.frequency, t0, 260 * this.j(0.03), 58, 0.45);
    const lp = biquad(ctx, 'lowpass', 900, 4.0);
    sweep(lp.frequency, t0, 1600, 220, 0.5);
    const g = gain(ctx, 0);
    percEnv(g.gain, t0, 0.004, 0.5, local ? 0.38 : 0.22);
    o.connect(lp);
    lp.connect(g);
    g.connect(bus);
    play(o, t0, t0 + 0.9);

    const n = this.noiseBurst('brown', t0, 0.3, this.j(0.1));
    const nlp = biquad(ctx, 'lowpass', 600, 1.4);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0008, 0.2, 0.68);
    n.connect(nlp);
    nlp.connect(ng);
    ng.connect(bus);

    if (local) {
      // A tinnitus-adjacent ring that fades out — you got hit and you know it.
      const ring = osc(ctx, 'sine', 3100);
      const rg = gain(ctx, 0);
      percEnv(rg.gain, t0, 0.004, 1.1, 0.055);
      ring.connect(rg);
      rg.connect(bus);
      play(ring, t0, t0 + 1.4);
    }

    return 1.5;
  }

  /**
   * The wound heartbeat. One lub-dub. The engine calls it at an interval that shortens as
   * the pressure rises, so the tempo carries the information, not the sound itself.
   */
  heartbeat(t: SfxTarget, intensity: number): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const i = clamp01(intensity);
    const bus = gain(ctx, 0.75 + 0.6 * i);
    this.out(bus, t, 0);

    const beat = (at: number, level: number, hz: number) => {
      // A heartbeat is almost entirely under 100 Hz, which means on a laptop speaker it is
      // almost entirely nothing. The knock in `sub` is the only reason a wounded player on
      // integrated audio can hear their own pulse at all.
      this.sub(bus, at, { from: hz * 1.6, to: hz * 0.62, sweep: 0.055, decay: 0.15, level, knock: 0.8 });

      // The muscular "thud" over the tone.
      const n = this.noiseBurst('brown', at, 0.12, this.j(0.1));
      const lp = biquad(ctx, 'lowpass', 260, 1.2);
      const ng = gain(ctx, 0);
      percEnv(ng.gain, at, 0.003, 0.08, level * 0.55);
      n.connect(lp);
      lp.connect(ng);
      ng.connect(bus);
    };

    const pitch = lerpN(46, 56, i);
    beat(t0, 0.6, pitch);
    beat(t0 + lerpN(0.19, 0.14, i), 0.42, pitch * 0.92);

    return 0.6;
  }

  /** Catching a chambered gun while wounded heals you. Warm, resolving, a genuine relief. */
  heal(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, t.local ? 1.1 : 0.7);
    this.out(bus, t, 0.5, t0);
    // A soft breath of weight under the chime. Relief still has a body to it, and without
    // this the heal was pure sparkle with nothing holding it to the ground.
    this.sub(bus, t0, { from: 120, to: 82, sweep: 0.14, decay: 0.4, level: 0.34, knock: 0.4 });
    this.healSwell(bus, t0);
    return 1.6;
  }

  private healSwell(dest: AudioNode, t0: number): void {
    const ctx = this.ctx;
    // A minor add9 rising to the fifth: warm without being saccharine.
    const notes = [noteHz(24), noteHz(31), noteHz(36), noteHz(43)];
    notes.forEach((hz, i) => {
      const at = t0 + i * 0.055;
      const fm = makeFm(ctx, hz, 2.0, hz * 0.5, 'sine', 'sine');
      const g = gain(ctx, 0);
      applyADSR(g.gain, at, ENV_CHIME, 0.18, 0.2 - i * 0.03);
      percEnv(fm.index.gain, at, 0.004, 0.3, hz * 0.9);
      fm.output.connect(g);
      g.connect(dest);
      fm.start(at);
      fm.stop(at + 1.1);
    });

    // A soft air swell underneath so the chime has somewhere to sit.
    const n = this.noiseBurst('pink', t0, 0.6);
    const bp = biquad(ctx, 'bandpass', 1800, 1.2);
    sweep(bp.frequency, t0, 900, 3200, 0.4);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.12, 0.4, 0.09);
    n.connect(bp);
    bp.connect(ng);
    ng.connect(dest);
  }

  // -------------------------------------------------------------------------
  // AMBIENCE — the world bed
  // -------------------------------------------------------------------------

  /**
   * The sound of the arena existing.
   *
   * "Sparseness is itself a bug... A player standing still in silence is a broken mix."
   * (DESIGN.md §5.6.) Three layers, all head-locked because they are properties of where the
   * listener is rather than of any point in the world:
   *
   *  ROOM   The atrium's own resonance. Brown noise under a very low lowpass with two
   *         resonant peaks at the room's modes, plus an HVAC drone. This is the layer you
   *         only notice when it stops.
   *  CITY   A bright white world with a sky in it is a building in a city. Heavily
   *         lowpassed pink noise breathing on two incommensurate periods, very quiet,
   *         plus a distant traffic shimmer that never quite resolves into anything.
   *  WIND   Rises with speed and with height. It is the arena telling you how fast you are
   *         going and how far you are from the floor, which is information, not decoration.
   *
   * Everything is driven by setters from the engine's per-frame update, so this costs a
   * handful of AudioParam writes per frame and nothing else.
   */
  ambience(): AmbienceVoice {
    const ctx = this.ctx;
    const t0 = this.now;
    const out = gain(ctx, 0);
    const duckGain = gain(ctx, 1);
    duckGain.connect(out);

    // --- ROOM ---------------------------------------------------------------
    const roomLevel = gain(ctx, 0);
    const roomSrc = this.noise.loop('brown', 0.35);
    const roomLp = biquad(ctx, 'lowpass', 320, 0.8);
    // Two room modes. A big concrete atrium has strong low resonances and this is what
    // separates "a room tone" from "a noise floor".
    const mode1 = biquad(ctx, 'peaking', 58, 5.0, 9);
    const mode2 = biquad(ctx, 'peaking', 143, 3.5, 6);
    const roomHp = biquad(ctx, 'highpass', 34, 0.7);
    roomSrc.connect(roomLp);
    roomLp.connect(mode1);
    mode1.connect(mode2);
    mode2.connect(roomHp);
    roomHp.connect(roomLevel);
    roomLevel.connect(duckGain);

    // Air handling. A barely-there drone, detuned against itself so it never sits still.
    const hvac1 = osc(ctx, 'sine', 47.5);
    const hvac2 = osc(ctx, 'sine', 71.3);
    const hvacG = gain(ctx, 0.05);
    const hvacTrem = gain(ctx, 1);
    hvac1.connect(hvacG);
    hvac2.connect(hvacG);
    hvacG.connect(hvacTrem);
    hvacTrem.connect(roomLevel);
    const hvacLfo = makeLfo(ctx, 0.041, 0.35, 'sine');
    hvacLfo.connectTo(hvacTrem.gain);
    // The room breathing. 0.023 Hz is a 43-second period — long enough that the ear never
    // catches it looping, which is the whole trick with a bed that plays for a full match.
    const roomBreath = makeLfo(ctx, 0.023, 70, 'sine');
    roomBreath.connectTo(roomLp.frequency);

    // --- CITY ---------------------------------------------------------------
    const cityLevel = gain(ctx, 0);
    const citySrc = this.noise.loop('pink', 0.55);
    const cityLp = biquad(ctx, 'lowpass', 760, 0.6);
    const cityHp = biquad(ctx, 'highpass', 150, 0.7);
    const citySwell = gain(ctx, 1);
    citySrc.connect(cityLp);
    cityLp.connect(cityHp);
    cityHp.connect(citySwell);
    citySwell.connect(cityLevel);
    cityLevel.connect(duckGain);
    // Two swells with no common period: the bed never repeats itself over a 90 second round.
    const citySlow = makeLfo(ctx, 0.037, 0.3, 'sine');
    citySlow.connectTo(citySwell.gain);
    const cityFast = makeLfo(ctx, 0.113, 0.18, 'triangle');
    cityFast.connectTo(citySwell.gain);
    const cityTone = makeLfo(ctx, 0.061, 220, 'sine');
    cityTone.connectTo(cityLp.frequency);

    // --- WIND ---------------------------------------------------------------
    const windLevel = gain(ctx, 0);
    const windSrc = this.noise.loop('white', 0.8);
    const windBp = biquad(ctx, 'bandpass', 500, 0.8);
    const windLp = biquad(ctx, 'lowpass', 2400, 0.7);
    // Gusting rides its own node so the LFOs modulate around unity instead of fighting the
    // level automation — the same discipline the hum's tremolo uses.
    const gustA = gain(ctx, 1);
    windSrc.connect(windBp);
    windBp.connect(windLp);
    windLp.connect(windLevel);
    windLevel.connect(gustA);
    gustA.connect(duckGain);
    const gust1 = makeLfo(ctx, 0.19, 0.34, 'sine');
    gust1.connectTo(gustA.gain);
    const gust2 = makeLfo(ctx, 0.071, 0.22, 'triangle');
    gust2.connectTo(gustA.gain);

    // A whistle for high speed — edge tones off the architecture. Silent at rest.
    const whistle = osc(ctx, 'sine', 1450);
    const whistleG = gain(ctx, 0);
    whistle.connect(whistleG);
    whistleG.connect(gustA);
    const whistleLfo = makeLfo(ctx, 0.27, 130, 'sine');
    whistleLfo.connectTo(whistle.frequency);

    const start = t0 + 0.01;
    startAtRandomPhase(roomSrc, start, this.rng);
    startAtRandomPhase(citySrc, start, this.rng);
    startAtRandomPhase(windSrc, start, this.rng);
    try {
      hvac1.start(start);
      hvac2.start(start);
      whistle.start(start);
    } catch {
      /* already started */
    }
    for (const l of [hvacLfo, roomBreath, citySlow, cityFast, cityTone, gust1, gust2, whistleLfo]) l.start(start);

    // The bed comes up over a second — a room tone that snaps on announces itself, and this
    // layer's entire job is to be believed rather than noticed.
    out.gain.setValueAtTime(MIN_GAIN, start);
    out.gain.exponentialRampToValueAtTime(1, start + 1.2);

    let stopped = false;

    return {
      output: out,
      setRoom(v: number) {
        if (stopped) return;
        roomLevel.gain.setTargetAtTime(clamp01(v) * 0.5, ctx.currentTime, 0.4);
      },
      setCity(v: number) {
        if (stopped) return;
        cityLevel.gain.setTargetAtTime(clamp01(v) * 0.13, ctx.currentTime, 0.6);
      },
      setWind(speed01: number, altitude01: number) {
        if (stopped) return;
        const s = clamp01(speed01);
        const a = clamp01(altitude01);
        const now = ctx.currentTime;
        // Speed dominates; altitude is a floor that is always there when you are high up.
        const drive = clamp01(s * 0.78 + a * 0.5);
        windLevel.gain.setTargetAtTime(0.012 + 0.3 * drive * drive, now, 0.16);
        windBp.frequency.setTargetAtTime(320 + 1500 * drive, now, 0.2);
        windBp.Q.setTargetAtTime(0.7 + 1.6 * s, now, 0.25);
        windLp.frequency.setTargetAtTime(1400 + 7000 * drive, now, 0.22);
        windSrc.playbackRate.setTargetAtTime(0.7 + 0.7 * drive, now, 0.3);
        // The whistle only exists at genuinely high speed, which makes it a reward.
        whistleG.gain.setTargetAtTime(clamp01((s - 0.62) / 0.38) * 0.028, now, 0.3);
      },
      duck(amount: number, seconds: number) {
        if (stopped) return;
        const now = ctx.currentTime;
        const floor = clamp(1 - amount, 0.04, 1);
        const p = duckGain.gain;
        p.cancelScheduledValues(now);
        p.setValueAtTime(Math.max(Math.min(p.value, 1), MIN_GAIN), now);
        // See `Music.duck` — the hole must exist before the transient reaches it.
        p.linearRampToValueAtTime(floor, now + 0.005);
        p.setValueAtTime(floor, now + Math.max(seconds * 0.3, 0.05));
        p.linearRampToValueAtTime(1, now + Math.max(seconds, 0.12));
      },
      stop(fadeSec = 0.6) {
        if (stopped) return;
        stopped = true;
        const now = ctx.currentTime;
        const end = fadeOut(out.gain, now, fadeSec);
        for (const s of [roomSrc, citySrc, windSrc, hvac1, hvac2, whistle]) stopSafe(s, end + 0.05);
        for (const l of [hvacLfo, roomBreath, citySlow, cityFast, cityTone, gust1, gust2, whistleLfo]) l.stop(end + 0.05);
        setTimeout(
          () => {
            try {
              out.disconnect();
            } catch {
              /* already gone */
            }
          },
          (fadeSec + 0.4) * 1000,
        );
      },
    };
  }

  /** Somebody died. A dropping impact with a ringing aftermath in the killer's register. */
  kill(t: SfxTarget, opts: { byLocal: boolean; onLocal: boolean; head: boolean }): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, opts.byLocal || opts.onLocal ? 1.3 : 0.92);
    this.out(bus, t, opts.onLocal ? 0.4 : 0.85, t0);

    // The impact frame, in audio. Hard front edge, then the body.
    this.transient(bus, t0, { level: 0.9, hp: 600, lp: 6000, tailMs: 5.0, grit: 0.7, decay: 0.018 });

    // The body hitting the floor of the world.
    this.sub(bus, t0, { from: 175, to: 33, sweep: 0.19, decay: 0.44, level: 1.1, knock: 0.65, shape: 0.8 });

    /**
     * THE HEADSHOT SIGNATURE — audible to everyone, not only the shooter.
     *
     * "HEAD KILLS · BODY WOUNDS" is one of the three things the game teaches, and it was
     * being carried entirely by a chime that only the killer heard. Anybody in earshot of a
     * headshot should know instantly that somebody just died outright rather than got
     * wounded: a hard bone crack on the front edge, then a bright struck-bell partial that
     * belongs to no other sound in the kit.
     */
    if (opts.head) {
      this.transient(bus, t0, { level: 0.85, hp: 2400, lp: 11000, tailMs: 1.8, grit: 0.25, decay: 0.006 });
      const bell = this.metal.source({
        freq: 3150 * this.j(0.03),
        seconds: 0.55,
        sustain: 0.55,
        brightness: 0.95,
        metal: 1.0,
        variant: (this.rng() * 3) | 0,
      });
      const bg = gain(ctx, 0);
      snapEnv(bg.gain, t0, 0.42, 0.42);
      const bhp = biquad(ctx, 'highpass', 1800, 0.7);
      bell.connect(bhp);
      bhp.connect(bg);
      bg.connect(bus);
      play(bell, t0, t0 + 0.7);
    }

    // The ink splash — a filtered noise splat, the audio half of the impact frame.
    const n = this.noiseBurst('white', t0, 0.5, this.j(0.08));
    const bp = biquad(ctx, 'bandpass', 2400 * this.j(0.08), 0.9);
    sweep(bp.frequency, t0, 4600, 500, 0.34);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t0, 0.0012, 0.3, 0.58);
    const dist = shaper(ctx, this.curveHard, '2x');
    n.connect(bp);
    bp.connect(dist);
    dist.connect(ng);
    ng.connect(bus);

    if (opts.onLocal) {
      // You died. Everything closes down: a descending sweep and a long muffled tail.
      const d = osc(ctx, 'sawtooth', 300);
      pitchEnv(d.frequency, t0, 300, 48, 1.1);
      const lp = biquad(ctx, 'lowpass', 1200, 6.0);
      sweep(lp.frequency, t0, 1800, 160, 1.2);
      const g = gain(ctx, 0);
      percEnv(g.gain, t0 + 0.02, 0.02, 1.3, 0.3);
      d.connect(lp);
      lp.connect(g);
      g.connect(bus);
      play(d, t0, t0 + 1.7);
    } else if (opts.byLocal) {
      // You got the kill. A short, bright confirmation on top of the impact.
      const hz = opts.head ? noteHz(43) : noteHz(36);
      [0, 0.05].forEach((off, i) => {
        const fm = makeFm(ctx, hz * (i === 0 ? 1 : 1.5), 1.41, hz * 1.4, 'sine', 'sine');
        const g = gain(ctx, 0);
        percEnv(g.gain, t0 + off, 0.001, 0.3, 0.24 - i * 0.06);
        percEnv(fm.index.gain, t0 + off, 0.001, 0.09, hz * 1.8);
        fm.output.connect(g);
        g.connect(bus);
        fm.start(t0 + off);
        fm.stop(t0 + off + 0.6);
      });
    }

    return opts.onLocal ? 2.0 : 1.3;
  }

  // -------------------------------------------------------------------------
  // STINGERS
  // -------------------------------------------------------------------------

  /** Round start. A rising swell into a hard downbeat. */
  roundStart(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 0.95);
    this.out(bus, t, 0.6, t0);

    // Reverse swell.
    const n = this.noiseBurst('white', t0, 0.75);
    const bp = biquad(ctx, 'bandpass', 1200, 1.1);
    sweep(bp.frequency, t0, 500, 4200, 0.7);
    const ng = gain(ctx, 0);
    ng.gain.setValueAtTime(MIN_GAIN, t0);
    ng.gain.exponentialRampToValueAtTime(0.36, t0 + 0.68);
    ng.gain.exponentialRampToValueAtTime(MIN_GAIN, t0 + 0.82);
    ng.gain.setValueAtTime(0, t0 + 0.84);
    n.connect(bp);
    bp.connect(ng);
    ng.connect(bus);

    // Downbeat: root + fifth + octave stab with a sub drop.
    const hit = t0 + 0.7;
    [noteHz(0), noteHz(12), noteHz(19), noteHz(24)].forEach((hz, i) => {
      const o = osc(ctx, i < 2 ? 'sine' : 'sawtooth', hz);
      const lp = biquad(ctx, 'lowpass', 2600, 2.0);
      sweep(lp.frequency, hit, 5200, 900, 0.4);
      const g = gain(ctx, 0);
      applyADSR(g.gain, hit, ENV_STAB, 0.12, 0.3 / (i * 0.7 + 1));
      o.connect(lp);
      lp.connect(g);
      g.connect(bus);
      play(o, hit, hit + 0.7);
    });

    // The downbeat lands as an impact, not as a note starting. Transient first, then weight.
    this.transient(bus, hit, { level: 0.8, hp: 700, lp: 8000, tailMs: 4.0, grit: 0.55, decay: 0.014 });
    this.sub(bus, hit, { from: 115, to: 39, sweep: 0.24, decay: 0.55, level: 1.05, knock: 0.7, shape: 0.8 });

    return 2.4;
  }

  /** Round end. Major and rising if you won, minor and sinking if you did not. */
  roundEnd(t: SfxTarget, won: boolean, draw: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 0.95);
    this.out(bus, t, 0.75, t0);

    const chord = draw ? [0, 7, 12] : won ? [0, 7, 16, 19] : [0, 6, 15];
    const dir = won ? 1 : -1;

    // A soft front edge so the chord arrives rather than fades in.
    this.transient(bus, t0, { level: won ? 0.45 : 0.28, hp: 900, lp: 7000, tailMs: 4.0, grit: 0.5, decay: 0.014 });

    chord.forEach((semi, i) => {
      const at = t0 + i * (won ? 0.06 : 0.09);
      const hz = noteHz(12 + semi);
      const o = osc(ctx, 'sawtooth', hz);
      if (!won && !draw) pitchEnv(o.frequency, at, hz, hz * 0.94, 0.9);
      const lp = biquad(ctx, 'lowpass', 2400, 2.5);
      sweep(lp.frequency, at, won ? 1600 : 2600, won ? 4200 : 700, 0.7);
      const g = gain(ctx, 0);
      applyADSR(g.gain, at, { a: won ? 0.006 : 0.05, d: 0.3, s: 0.35, r: 0.7 }, 0.5, 0.2);
      o.connect(lp);
      lp.connect(g);
      g.connect(bus);
      play(o, at, at + 1.8);
    });

    const sub = osc(ctx, 'sine', 80);
    pitchEnv(sub.frequency, t0, 80, 80 * (dir > 0 ? 1.5 : 0.62), 0.5);
    const sg = gain(ctx, 0);
    percEnv(sg.gain, t0, 0.008, 0.7, 0.6);
    sub.connect(sg);
    sg.connect(bus);
    play(sub, t0, t0 + 1.4);

    return 2.6;
  }

  /**
   * HIGH NOON. Every gun in the arena lights up and goes loud. The sound has to say
   * "hiding no longer works": a descending ring-modulated gong under a rising siren.
   */
  suddenDeath(t: SfxTarget): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1.05);
    this.out(bus, t, 0.95, t0);

    // The strike. A gong is a hammer hitting bronze, and the hammer was missing.
    this.transient(bus, t0, { level: 0.7, hp: 400, lp: 5000, tailMs: 6.0, grit: 0.7, decay: 0.02 });

    // Gong.
    const rm = makeRingMod(ctx, 97, 'sine');
    [1, 1.73, 2.41, 3.87].forEach((r, i) => {
      const o = osc(ctx, 'sine', 82 * r);
      pitchEnv(o.frequency, t0, 92 * r, 76 * r, 1.6);
      const g = gain(ctx, 0.6 / (i + 1));
      o.connect(g);
      g.connect(rm.input);
      play(o, t0, t0 + 2.6);
    });
    const gongG = gain(ctx, 0);
    percEnv(gongG.gain, t0, 0.004, 2.2, 0.5);
    rm.output.connect(gongG);
    gongG.connect(bus);
    rm.start(t0);
    rm.stop(t0 + 2.7);

    // Siren rising through the gong — the information escalation.
    const s = osc(ctx, 'sawtooth', 300);
    pitchEnv(s.frequency, t0 + 0.2, 220, 880, 1.5);
    const bp = biquad(ctx, 'bandpass', 1200, 6.0);
    sweep(bp.frequency, t0 + 0.2, 700, 3000, 1.5);
    const sg = gain(ctx, 0);
    sg.gain.setValueAtTime(MIN_GAIN, t0 + 0.2);
    sg.gain.exponentialRampToValueAtTime(0.16, t0 + 1.4);
    sg.gain.exponentialRampToValueAtTime(MIN_GAIN, t0 + 2.2);
    sg.gain.setValueAtTime(0, t0 + 2.25);
    s.connect(bp);
    bp.connect(sg);
    sg.connect(bus);
    play(s, t0 + 0.2, t0 + 2.4);

    // Sub drop to seat it.
    this.sub(bus, t0, { from: 130, to: 32, sweep: 0.6, decay: 1.3, level: 1.0, knock: 0.6, shape: 0.85 });

    return 3.4;
  }

  matchEnd(t: SfxTarget, won: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1.0);
    this.out(bus, t, 0.85, t0);
    this.transient(bus, t0, { level: won ? 0.5 : 0.3, hp: 800, lp: 7000, tailMs: 4.5, grit: 0.5, decay: 0.016 });

    const chord = won ? [0, 7, 12, 16, 19, 24] : [0, 3, 7, 10];
    chord.forEach((semi, i) => {
      const at = t0 + i * 0.07;
      const hz = noteHz(12 + semi);
      const o = osc(ctx, 'sawtooth', hz, i % 2 ? 5 : -5);
      const lp = biquad(ctx, 'lowpass', 2200, 1.8);
      sweep(lp.frequency, at, won ? 1400 : 2200, won ? 5000 : 500, 1.2);
      const g = gain(ctx, 0);
      applyADSR(g.gain, at, { a: won ? 0.008 : 0.09, d: 0.5, s: 0.4, r: 1.3 }, 1.0, 0.17);
      o.connect(lp);
      lp.connect(g);
      g.connect(bus);
      play(o, at, at + 3.0);
    });

    const sub = osc(ctx, 'sine', 55);
    pitchEnv(sub.frequency, t0, won ? 55 : 110, won ? 110 : 41, 0.8);
    const sg = gain(ctx, 0);
    percEnv(sg.gain, t0, 0.01, 1.6, 0.7);
    sub.connect(sg);
    sg.connect(bus);
    play(sub, t0, t0 + 2.6);

    return 4.0;
  }

  // -------------------------------------------------------------------------
  // UI
  // -------------------------------------------------------------------------

  ui(dest: AudioNode, kind: 'hover' | 'click' | 'back' | 'error' | 'ready' | 'countdown'): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1);
    bus.connect(dest);

    const blip = (at: number, hz: number, level: number, decay: number, type: OscillatorType = 'sine') => {
      const o = osc(ctx, type, hz);
      const g = gain(ctx, 0);
      percEnv(g.gain, at, 0.0012, decay, level);
      const bp = biquad(ctx, 'bandpass', hz, 2.2);
      o.connect(bp);
      bp.connect(g);
      g.connect(bus);
      play(o, at, at + decay + 0.06);
    };

    const tick = (at: number, level: number) => {
      const cl = this.click.source(2.0, 0.35, 1);
      const hp = biquad(ctx, 'highpass', 2200, 0.7);
      const g = gain(ctx, 0);
      percEnv(g.gain, at, 0.0003, 0.01, level);
      cl.connect(hp);
      hp.connect(g);
      g.connect(bus);
      play(cl, at, at + 0.05);
    };

    switch (kind) {
      case 'hover':
        blip(t0, 2640, 0.05, 0.03, 'triangle');
        return 0.15;
      case 'click':
        tick(t0, 0.3);
        blip(t0, 1320, 0.16, 0.05, 'triangle');
        blip(t0 + 0.012, 1980, 0.09, 0.04, 'sine');
        return 0.2;
      case 'back':
        tick(t0, 0.2);
        blip(t0, 990, 0.14, 0.06, 'triangle');
        blip(t0 + 0.03, 660, 0.1, 0.08, 'sine');
        return 0.25;
      case 'error': {
        const o = osc(ctx, 'sawtooth', 155);
        const o2 = osc(ctx, 'sawtooth', 164);
        const lp = biquad(ctx, 'lowpass', 900, 3.0);
        const g = gain(ctx, 0);
        percEnv(g.gain, t0, 0.004, 0.22, 0.16, 0.05);
        o.connect(lp);
        o2.connect(lp);
        lp.connect(g);
        g.connect(bus);
        play(o, t0, t0 + 0.5);
        play(o2, t0, t0 + 0.5);
        return 0.5;
      }
      case 'ready':
        blip(t0, noteHz(31), 0.16, 0.09, 'triangle');
        blip(t0 + 0.07, noteHz(36), 0.16, 0.14, 'triangle');
        return 0.35;
      case 'countdown':
        tick(t0, 0.22);
        blip(t0, noteHz(24), 0.18, 0.16, 'triangle');
        return 0.35;
      default:
        return 0.1;
    }
  }

  /** Someone joined or left the lobby. */
  presence(dest: AudioNode, joined: boolean): number {
    const ctx = this.ctx;
    const t0 = this.now + 0.001;
    const bus = gain(ctx, 1);
    bus.connect(dest);
    const notes = joined ? [noteHz(24), noteHz(31)] : [noteHz(31), noteHz(24)];
    notes.forEach((hz, i) => {
      const o = osc(ctx, 'triangle', hz);
      const g = gain(ctx, 0);
      percEnv(g.gain, t0 + i * 0.06, 0.002, 0.1, 0.11);
      o.connect(g);
      g.connect(bus);
      play(o, t0 + i * 0.06, t0 + i * 0.06 + 0.25);
    });
    return 0.35;
  }

  dispose(): void {
    this.click.dispose();
    this.metal.dispose();
    this.noise.dispose();
  }
}

/** Stable per-string variation seed, so a given player's footsteps always sound like theirs. */
export const voiceSeedFor = (id: string): number => hashString(id) >>> 0;
